import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { format, addDays, subDays } from "date-fns";
import { tr } from "date-fns/locale";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Ship, Phone, Package,
  Lock, Unlock, GripVertical, CheckCircle, XCircle, Clock,
  Save, RefreshCw, Sun, Sunset, Users, UtensilsCrossed,
  AlertTriangle, Settings, MessageCircle, Send, X, Loader2, Wine, Car, Star, Mail, Download, Tag, Wand2
} from "lucide-react";
import { Link } from "react-router-dom";
import RescheduleModal from "../components/RescheduleModal";

const API = window.location.origin;

// Drag handle tracking - only allow drag from grip icon
let _dragHandleActive = false;

const STATUS_OPTIONS = [
  { value: "pending", label: "Bekliyor", bg: "#FEF3C7", text: "#92400E" },
  { value: "checked-in", label: "Geldi", bg: "#D1FAE5", text: "#065F46" },
  { value: "no_show", label: "Gelmedi", bg: "#FEF3C7", text: "#B45309" },
  { value: "cancelled", label: "Iptal", bg: "#FEE2E2", text: "#991B1B" },
];

const CATEGORY_ICONS = {
  "utensils": UtensilsCrossed,
  "sunset": Sunset,
  "users": Users,
  "sun": Sun,
};

const MSG_STATUS_OPTIONS = [
  { type: "mesaj_gonderildi", label: "Gonderildi", bg: "bg-green-500", text: "text-white" },
  { type: "wp_yok", label: "WP Yok", bg: "bg-red-500", text: "text-white" },
  { type: "numara_yanlis", label: "No Yanlis", bg: "bg-orange-500", text: "text-white" },
  { type: "ulasamadi", label: "Ulasamadi", bg: "bg-amber-500", text: "text-white" },
  { type: "aradi", label: "Arandi", bg: "bg-blue-500", text: "text-white" },
  { type: "cevap_yok", label: "Cevap Yok", bg: "bg-gray-500", text: "text-white" },
];

function MessageStatusBtn({ phone, bookingId, customerName, staffNotes, onSelect, onRemove }) {
  const [open, setOpen] = useState(false);
  const btnRef = React.useRef(null);
  const noteKey = phone || `bid_${bookingId}`;
  const notes = staffNotes[noteKey] || [];
  const statusNote = notes.find(n => MSG_STATUS_OPTIONS.some(o => o.type === n.note_type));
  const currentOption = statusNote ? MSG_STATUS_OPTIONS.find(o => o.type === statusNote.note_type) : null;

  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  const handleOpen = (e) => {
    e.stopPropagation();
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuH = 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow > menuH) {
        setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
      } else {
        setMenuPos({ top: rect.top - menuH - 4, right: window.innerWidth - rect.right });
      }
    }
    setOpen(!open);
  };

  return (
    <div className="shrink-0">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className={`px-1.5 py-0.5 rounded text-[9px] font-bold leading-tight whitespace-nowrap ${currentOption ? `${currentOption.bg} ${currentOption.text}` : "bg-gray-200 text-gray-500"}`}
        data-testid={`msg-status-${bookingId}`}
      >
        {currentOption ? currentOption.label : "Durum"}
      </button>
      {open && ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="fixed bg-white rounded-lg shadow-2xl border z-[9999] w-40 py-1" style={{ top: menuPos.top, right: menuPos.right }} onClick={e => e.stopPropagation()}>
            {MSG_STATUS_OPTIONS.map(opt => (
              <button
                key={opt.type}
                onClick={(e) => { e.stopPropagation(); onSelect(opt.type); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${statusNote?.note_type === opt.type ? "font-bold bg-gray-100" : ""}`}
                data-testid={`msg-opt-${opt.type}`}
              >
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${opt.bg} ${opt.text}`}>{opt.label}</span>
              </button>
            ))}
            {statusNote && (
              <>
                <div className="border-t my-1" />
                <button onClick={(e) => { e.stopPropagation(); onRemove(statusNote.id); setOpen(false); }} className="w-full text-left px-3 py-2.5 text-xs text-red-500 hover:bg-red-50">
                  Kaldir
                </button>
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

function SwipeCard({ children, onSwipeRight, onSwipeLeft, rightLabel, leftLabel, rightColor, leftColor, disabled }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchDelta, setTouchDelta] = useState(0);
  const [swiping, setSwiping] = useState(false);

  const threshold = 80;

  const handleTouchStart = (e) => {
    if (disabled) return;
    setTouchStart(e.touches[0].clientX);
    setSwiping(true);
  };
  const handleTouchMove = (e) => {
    if (!swiping || touchStart === null) return;
    const delta = e.touches[0].clientX - touchStart;
    setTouchDelta(delta);
  };
  const handleTouchEnd = () => {
    if (!swiping) return;
    if (touchDelta > threshold && onSwipeRight) onSwipeRight();
    if (touchDelta < -threshold && onSwipeLeft) onSwipeLeft();
    setTouchDelta(0);
    setTouchStart(null);
    setSwiping(false);
  };

  const clampedDelta = Math.max(-120, Math.min(120, touchDelta));
  const isRight = clampedDelta > 30;
  const isLeft = clampedDelta < -30;
  const bgColor = isRight ? (rightColor || "#DCFCE7") : isLeft ? (leftColor || "#DBEAFE") : "transparent";
  const opacity = Math.min(Math.abs(clampedDelta) / threshold, 1);

  return (
    <div className="relative overflow-hidden rounded-lg" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {/* Swipe background indicators */}
      {(isRight || isLeft) && (
        <div className="absolute inset-0 flex items-center z-0 transition-opacity" style={{ backgroundColor: bgColor, opacity: opacity * 0.8 }}>
          {isRight && (
            <div className="flex items-center gap-2 pl-4 text-green-800">
              <CheckCircle className="w-5 h-5" />
              <span className="text-xs font-bold">{rightLabel || "Check-in"}</span>
            </div>
          )}
          {isLeft && (
            <div className="flex items-center gap-2 pr-4 ml-auto text-blue-800">
              <span className="text-xs font-bold">{leftLabel || "Ara"}</span>
              <Phone className="w-5 h-5" />
            </div>
          )}
        </div>
      )}
      {/* Card content */}
      <div className="relative z-10 transition-transform" style={{ transform: `translateX(${clampedDelta}px)` }}>
        {children}
      </div>
    </div>
  );
}

function extractAgency(productName) {
  if (!productName) return "-";
  const p = productName.toLowerCase();
  if (p.startsWith("gyg") || p.includes("gyg")) return "GYG";
  if (p.includes("klook")) return "KLOOK";
  if (p.includes("viator") || p.includes("bosphorus")) return "VIATOR";
  if (p.includes("headout")) return "HEADOUT";
  if (p.includes("lufer") || p.includes("p2")) return "LUFER";
  if (p.includes("masal")) return "MASAL";
  if (p.includes("smallgroup")) return "SMALLGRP";
  return "DIGER";
}

function getOptionDesc(productName) {
  if (!productName) return "";
  let p = productName.trim();
  const lower = p.toLowerCase();
  const prefixes = ["gyg ", "klook ", "masal", "p2"];
  for (const pfx of prefixes) {
    if (lower.startsWith(pfx)) { p = p.substring(pfx.length).trim(); break; }
  }
  return p;
}

function isTransferProduct(productName) {
  if (!productName) return false;
  const p = productName.toLowerCase();
  return p.includes("transferli") || p.includes("transfer ");
}

function isKendiGelir(productName) {
  if (!productName) return false;
  const p = productName.toLowerCase();
  return p.includes("kendi gelir") || p.includes("kendigelir") || p.includes("transfersiz");
}

function extrasText(extras) {
  if (!extras || !extras.length) return "";
  return extras.map(e => (e.extra_name || e.name || "")).join(" ").toLowerCase();
}

const _EXTRA_SHORT = [
  ["windowed view table", "Cam Masa"], ["window view", "Cam Masa"], ["window table", "Cam Masa"],
  ["vip lounge table", "VIP Masa"], ["vip lounge", "VIP Lounge"], ["vip table", "VIP Masa"],
  ["private table", "Özel Masa"], ["premium table", "Premium Masa"],
  ["private transfer", "Özel Transfer"], ["hotel transfer", "Otel Transfer"],
  ["round trip transfer", "Gidiş-Dönüş"], ["one way transfer", "Tek Yön"], ["transfer", "Transfer"],
  ["birthday", "Doğum Günü"], ["anniversary", "Yıldönümü"], ["honeymoon", "Balayı"],
  ["cake", "Pasta"], ["photo", "Fotoğraf"], ["flower", "Çiçek"],
  ["champagne", "Şampanya"], ["wine", "Şarap"], ["soft drink", "İçecek"],
];
function shortExtra(name) {
  if (!name) return "";
  const low = String(name).toLowerCase();
  for (const [k, s] of _EXTRA_SHORT) if (low.includes(k)) return s;
  return String(name).replace(/\s*\((per person|per booking|kişi başı|per pax)\)\s*/i, "").trim().slice(0, 18);
}
// Ayni turleri birlestirip [{label, qty}] dondurur
function shortExtras(extras) {
  if (!extras || !extras.length) return [];
  const agg = {};
  const order = [];
  for (const e of extras) {
    const nm = e.extra_name || e.name || "";
    if (!nm) continue;
    const s = shortExtra(nm);
    if (!(s in agg)) { agg[s] = 0; order.push(s); }
    agg[s] += (e.quantity || e.qty || 1);
  }
  return order.map(s => ({ label: s, qty: agg[s] }));
}
const _EXTRA_COLORS = {
  "Cam Masa": "#0284C7", "VIP Masa": "#7C3AED", "VIP Lounge": "#7C3AED",
  "Özel Masa": "#0D9488", "Premium Masa": "#0D9488",
  "Transfer": "#059669", "Özel Transfer": "#059669", "Otel Transfer": "#059669", "Gidiş-Dönüş": "#059669", "Tek Yön": "#059669",
  "Pasta": "#DB2777", "Çiçek": "#DB2777", "Doğum Günü": "#F59E0B", "Yıldönümü": "#F59E0B", "Balayı": "#F59E0B",
  "Fotoğraf": "#64748B", "Şampanya": "#B91C1C", "Şarap": "#B91C1C", "İçecek": "#0891B2",
};
function extraColor(label) { return _EXTRA_COLORS[label] || "#6B7280"; }

function parseOptionBadges(optionName, productName, extras) {
  const badges = [];
  const text = ((optionName || "") + " " + (productName || "")).toLowerCase();
  const exText = extrasText(extras);
  
  // Transfer / Kendi Gelir detection
  if (text.includes("transferli") || text.includes("with transfer") || text.includes("hotel transfer") || text.includes("and transfer") || text.includes("alcohole transfer")) {
    badges.push({ label: "Transferli", bg: "#2563EB", color: "#fff", title: "Transferli" });
  } else if (text.includes("kendi gelir") || text.includes("kendigelir") || text.includes("no transfer") || text.includes("meeting point")) {
    badges.push({ label: "Kendi Gelir", bg: "#6B7280", color: "#fff", title: "Kendi Gelir" });
  }
  
  // VIP
  if (text.includes("vip")) {
    badges.push({ label: "VIP", bg: "#7C3AED", color: "#fff", title: "VIP" });
  }
  
  // Limitsiz / Unlimited
  if (text.includes("limitsiz") || text.includes("unlimited")) {
    badges.push({ label: "Limitsiz", bg: "#DC2626", color: "#fff", title: "Limitsiz" });
  }
  
  // Drink type detection
  if (text.includes("alkollü") || text.includes("alkollu") || text.includes("alcoholic") || text.includes("alcohole") || text.includes("limited alcohol")) {
    badges.push({ label: "Alkollu", bg: "#EA580C", color: "#fff", title: "Alkollu" });
  } else if (text.includes("alkolsüz") || text.includes("alkolsuz") || text.includes("non-alcohol") || text.includes("soft drink")) {
    badges.push({ label: "Alkolsuz", bg: "#16A34A", color: "#fff", title: "Alkolsuz" });
  }
  
  // Romantic
  if (text.includes("romantic")) {
    badges.push({ label: "Romantic", bg: "#DB2777", color: "#fff", title: "Romantic" });
  }
  
  // Snack
  if (text.includes("snack")) {
    badges.push({ label: "Snack", bg: "#0891B2", color: "#fff", title: "Snack" });
  }

  // Private table / Ozel masa
  if (text.includes("private table") || text.includes("özel masa") || text.includes("ozel masa")) {
    badges.push({ label: "PVT", bg: "#0E7490", color: "#fff", title: "Private Table" });
  }

  // Window seat / Cam kenari — opsiyon/urun adi VEYA addon (Windowed View Table) icinde
  if (text.includes("window") || text.includes("cam kenar") || text.includes("p2dinner") || text.includes("p2 dinner")
      || exText.includes("window") || exText.includes("cam kenar")) {
    badges.push({ label: "Cam Kenari", bg: "#B45309", color: "#fff", title: "Cam Kenari" });
  }

  // VIP Lounge (VIP Menu'den ayri premium)
  if (text.includes("vip lounge") || text.includes("vip loca")) {
    badges.push({ label: "VIP Lounge", bg: "#4C1D95", color: "#fff", title: "VIP Lounge" });
  }

  // Yemek durumu — Yemeksiz / Yemekli (birbirini disler)
  if (text.includes("yemeksiz") || text.includes("no meal")) {
    badges.push({ label: "Yemeksiz", bg: "#57534E", color: "#fff", title: "Yemeksiz (yemek yok)" });
  } else if (text.includes("yemekli") || text.includes("with meal")) {
    badges.push({ label: "Yemekli", bg: "#65A30D", color: "#fff", title: "Yemekli" });
  }

  // Kalkis / bulusma noktalari (Turkce harfler icin aksansiz eslesme)
  const ntext = text.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  if (ntext.includes("kabatas")) {
    badges.push({ label: "Kabatas", bg: "#1E40AF", color: "#fff", title: "Kalkis: Kabatas" });
  }
  if (ntext.includes("cibali")) {
    badges.push({ label: "Cibali", bg: "#4338CA", color: "#fff", title: "Kalkis: Cibali" });
  }

  return badges;
}

function getAllBadges(optionName, productName, customTags, extras) {
  const badges = parseOptionBadges(optionName, productName, extras);
  if (!customTags || customTags.length === 0) return badges;
  const text = ((optionName || "") + " " + (productName || "")).toLowerCase();
  for (const ct of customTags) {
    const pattern = (ct.match_pattern || "").toLowerCase();
    if (!pattern) continue;
    let searchIn = text;
    if (ct.match_field === "option_name") searchIn = (optionName || "").toLowerCase();
    else if (ct.match_field === "product_name") searchIn = (productName || "").toLowerCase();
    if (searchIn.includes(pattern) && !badges.find(b => b.label === ct.tag_label)) {
      badges.push({ label: ct.tag_label, bg: ct.tag_color, color: "#fff", title: ct.tag_label, custom: true });
    }
  }
  return badges;
}


function shortProductLabel(productName) {
  if (!productName) return "";
  // Show full name without truncation
  return productName
    .replace(/^gyg\s*/i, "")
    .replace(/^klook\s*/i, "")
    .replace(/^viator\s*/i, "")
    .replace(/^headout\s*/i, "")
    .replace(/^P2/i, "")
    .replace(/^masal/i, "")
    .trim();
}

function InlineEdit({ value, onSave, locked, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);

  if (locked || !editing) {
    return (
      <div
        className={`cursor-pointer min-h-[20px] ${className}`}
        onClick={() => !locked && setEditing(true)}
        title={locked ? "Kilitli" : "Tikla"}
      >
        {value || ""}
      </div>
    );
  }
  return (
    <input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { setEditing(false); if (val !== (value || "")) onSave(val); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { setEditing(false); if (val !== (value || "")) onSave(val); }
        if (e.key === "Escape") { setEditing(false); setVal(value || ""); }
      }}
      className="w-full border border-blue-500 bg-blue-50 text-[12px] px-1 py-0 outline-none text-gray-900"
      style={{ minWidth: 40 }}
    />
  );
}

export default function OperationsSheet() {
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState({ boats: [], rows: [], categories: [], total: 0, unmapped_count: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [dragRow, setDragRow] = useState(null);
  const [dragOverBoat, setDragOverBoat] = useState(null);
  const [whatsappModal, setWhatsappModal] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [phoneStatuses, setPhoneStatuses] = useState({});
  const [validating, setValidating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Bulk selection
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  // Boat management
  const [showBoatManager, setShowBoatManager] = useState(false);
  const [editingBoat, setEditingBoat] = useState(null);
  const [newBoatName, setNewBoatName] = useState("");
  // Staff filter
  const [staffAssignments, setStaffAssignments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [activeStaffFilter, setActiveStaffFilter] = useState(null); // null = show all, assignment_id = filtered
  const [staffNotes, setStaffNotes] = useState({});
  const [noteModal, setNoteModal] = useState(null);
  const [showMessageList, setShowMessageList] = useState(false);
  const [customTags, setCustomTags] = useState([]);
  const [activeTagFilters, setActiveTagFilters] = useState([]); // secili addon/etiket filtreleri (AND)

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileActions, setMobileActions] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const dateStr = format(date, "yyyy-MM-dd");
  const dateDisplay = format(date, "d MMMM yyyy, EEEE", { locale: tr });

  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchSheet = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeCategory && activeCategory !== "unmapped" ? `?category_id=${activeCategory}` : "";
      const { data: d } = await axios.get(`${API}/api/sheet/${dateStr}${params}`, { headers });
      setData(d);
    } catch (err) {
      console.error("Sheet fetch error:", err);
    }
    setLoading(false);
  }, [dateStr, activeCategory]);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  // Fetch staff assignments and notes
  useEffect(() => {
    axios.get(`${API}/api/staff/assignments`, { headers }).then(res => {
      setStaffAssignments(res.data?.assignments || []);
    }).catch(() => {});
    axios.get(`${API}/api/staff`, { headers }).then(res => {
      setStaff(res.data?.staff || []);
    }).catch(() => {});
    axios.get(`${API}/api/staff/notes`, { headers }).then(res => {
      const notesMap = {};
      (res.data?.notes || []).forEach(n => {
        const key = n.customer_phone || `bid_${n.booking_id}`;
        if (!notesMap[key]) notesMap[key] = [];
        notesMap[key].push(n);
      });
      setStaffNotes(notesMap);
    }).catch(() => {});
    axios.get(`${API}/api/custom-tags`, { headers }).then(res => {
      setCustomTags((res.data?.tags || []).filter(t => t.active));
    }).catch(() => {});
  }, [dateStr]);

  // Match row to assigned staff members
  const getAssignedStaff = useCallback((row) => {
    const badges = parseOptionBadges(row.option_name, row.product_name).map(b => b.label);
    const agency = extractAgency(row.product_name);
    const catSlug = (data.categories || []).find(c => c.id === row.category_id)?.slug || "";
    const matched = [];
    for (const a of staffAssignments) {
      const rules = a.filter_rules || {};
      let match = true;
      if (rules.categories?.length) match = match && rules.categories.some(c => catSlug.toLowerCase().includes(c.toLowerCase()));
      if (rules.badges?.length) match = match && rules.badges.some(b => badges.includes(b));
      if (rules.agencies?.length) match = match && rules.agencies.includes(agency);
      if (rules.products?.length) match = match && rules.products.some(p => (row.product_name || "").toLowerCase().trim() === p.toLowerCase().trim());
      if (match) {
        const s = staff.find(st => st.id === a.user_id);
        if (s) matched.push({ name: s.name, assignment: a.assignment_name, user_id: a.user_id, assignment_id: a.id });
      }
    }
    return matched;
  }, [staffAssignments, staff, data.categories]);

  // Staff filter logic — uses getAssignedStaff to show ONLY rows assigned to selected staff
  const applyStaffFilter = useCallback((rows) => {
    if (!activeStaffFilter) return rows;
    const assignment = staffAssignments.find(a => a.id === activeStaffFilter);
    if (!assignment) return rows;
    const targetUserId = assignment.user_id;
    return rows.filter(row => {
      const assigned = getAssignedStaff(row);
      return assigned.some(s => s.user_id === targetUserId);
    });
  }, [activeStaffFilter, staffAssignments, getAssignedStaff]);

  // Etiket/addon filtresi — secili tum etiketleri iceren satirlar (AND)
  const rowMatchesTags = useCallback((row) => {
    if (activeTagFilters.length === 0) return true;
    const labels = getAllBadges(row.option_name, row.product_name, customTags, row.extras).map(b => b.label);
    return activeTagFilters.every(t => labels.includes(t));
  }, [activeTagFilters, customTags]);

  // Bu tarihte gorunen tum etiketler + adetleri (filtre cipleri icin)
  const availableTags = useMemo(() => {
    const map = new Map();
    for (const r of (data.rows || [])) {
      if (r.status === "cancelled") continue;
      for (const b of getAllBadges(r.option_name, r.product_name, customTags)) {
        const e = map.get(b.label) || { label: b.label, bg: b.bg, count: 0 };
        e.count++; map.set(b.label, e);
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [data.rows, customTags]);

  const saveStaffNote = async (phone, bookingId, noteType, noteText) => {
    try {
      await axios.post(`${API}/api/staff/notes`, { customer_phone: phone, booking_id: bookingId, note_type: noteType, note_text: noteText }, { headers });
      // Refresh notes
      const res = await axios.get(`${API}/api/staff/notes`, { headers });
      const notesMap = {};
      (res.data?.notes || []).forEach(n => {
        const key = n.customer_phone || `bid_${n.booking_id}`;
        if (!notesMap[key]) notesMap[key] = [];
        notesMap[key].push(n);
      });
      setStaffNotes(notesMap);
      setNoteModal(null);
    } catch (e) { console.error(e); }
  };

  const toggleMessageSent = async (phone, bookingId, customerName, noteType, removeNoteId) => {
    if (removeNoteId) {
      // Remove specific note
      try {
        await axios.delete(`${API}/api/staff/notes/${removeNoteId}`, { headers });
        const res = await axios.get(`${API}/api/staff/notes`, { headers });
        const notesMap = {};
        (res.data?.notes || []).forEach(n => {
          const key = n.customer_phone || `bid_${n.booking_id}`;
          if (!notesMap[key]) notesMap[key] = [];
          notesMap[key].push(n);
        });
        setStaffNotes(notesMap);
      } catch (e) { console.error(e); }
      return;
    }
    // Save new status note (remove old status note first)
    const noteKey = phone || `bid_${bookingId}`;
    const existing = (staffNotes[noteKey] || []).find(n => MSG_STATUS_OPTIONS.some(o => o.type === n.note_type));
    if (existing) {
      try { await axios.delete(`${API}/api/staff/notes/${existing.id}`, { headers }); } catch {}
    }
    await saveStaffNote(phone, bookingId, noteType || "mesaj_gonderildi", customerName || "");
  };

  const isMessageMarked = (phone, bookingId) => {
    const noteKey = phone || `bid_${bookingId}`;
    return (staffNotes[noteKey] || []).some(n => n.note_type === "mesaj_gonderildi");
  };

  const quickSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/api/sync/day/${dateStr}`, {}, { headers });
      setTimeout(() => { fetchSheet(); setSyncing(false); }, 5000);
    } catch {
      setSyncing(false);
    }
  };

  const [autoAssigning, setAutoAssigning] = useState(false);
  const runAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      const body = { date: dateStr };
      if (activeCategory && activeCategory !== "unmapped") body.category_id = activeCategory;
      const { data } = await axios.post(`${API}/api/sheet/auto-assign`, body, { headers });
      alert(data.message || "Otomatik dağıtıldı");
      fetchSheet();
    } catch (e) {
      alert("Otomatik dağıtım başarısız: " + (e?.response?.data?.detail || ""));
    }
    setAutoAssigning(false);
  };

  const runRedistribute = async () => {
    if (!window.confirm("Mevcut atamalar yeni kurallara göre yeniden dizilecek.\n(Kilitli/reschedule, iptal ve Rezerve teknedeki satırlara dokunulmaz.)\nDevam edilsin mi?")) return;
    setAutoAssigning(true);
    try {
      const body = { date: dateStr, redistribute: true };
      if (activeCategory && activeCategory !== "unmapped") body.category_id = activeCategory;
      const { data } = await axios.post(`${API}/api/sheet/auto-assign`, body, { headers });
      alert(data.message || "Yeniden dağıtıldı");
      fetchSheet();
    } catch (e) {
      alert("Yeniden dağıtım başarısız: " + (e?.response?.data?.detail || ""));
    }
    setAutoAssigning(false);
  };

  const runHardRedistribute = async () => {
    if (!window.confirm("⚠️ HARD YENİDEN DAĞIT\n\nBugün ve SONRAKİ TÜM İLERİ TARİHLİ rezervasyonlar kurallara göre yeniden dağıtılacak.\n(Kilitli/reschedule, iptal ve Rezerve teknedeki satırlara dokunulmaz.)\n\nBu işlem geri alınamaz. Devam edilsin mi?")) return;
    setAutoAssigning(true);
    try {
      const body = { date: dateStr, redistribute: true, all_future: true };
      const { data } = await axios.post(`${API}/api/sheet/auto-assign`, body, { headers });
      alert(data.message || "Tüm ileri tarihler yeniden dağıtıldı");
      fetchSheet();
    } catch (e) {
      alert("Hard yeniden dağıtım başarısız: " + (e?.response?.data?.detail || ""));
    }
    setAutoAssigning(false);
  };

  // Bulk selection handlers
  const toggleRowSelect = (rowId) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const selectAllInBoat = (boatId) => {
    const boatRows = data.rows.filter(r => r.boat_id === boatId && r.status !== "cancelled");
    setSelectedRows(prev => {
      const next = new Set(prev);
      const allSelected = boatRows.every(r => next.has(r.id));
      if (allSelected) {
        boatRows.forEach(r => next.delete(r.id));
      } else {
        boatRows.forEach(r => next.add(r.id));
      }
      return next;
    });
  };

  const bulkMoveToBoat = async (targetBoatId) => {
    if (selectedRows.size === 0) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/sheet/bulk-move`, {
        row_ids: Array.from(selectedRows),
        target_boat_id: targetBoatId,
      }, { headers });
      setSelectedRows(new Set());
      setBulkMode(false);
      fetchSheet();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  // Boat management
  const addBoat = async () => {
    if (!newBoatName.trim()) return;
    try {
      await axios.post(`${API}/api/boats`, { name: newBoatName.trim() }, { headers });
      setNewBoatName("");
      fetchSheet();
    } catch (e) { console.error(e); }
  };

  const renameBoat = async (boatId, name) => {
    try {
      await axios.put(`${API}/api/boats/${boatId}`, { name }, { headers });
      setEditingBoat(null);
      fetchSheet();
    } catch (e) { console.error(e); }
  };

  const updateBoatCapacity = async (boatId, capacity) => {
    const cap = capacity === "" ? 0 : parseInt(capacity);
    if (isNaN(cap) || cap < 0) return;
    try {
      await axios.put(`${API}/api/boats/${boatId}`, { capacity: cap }, { headers });
      fetchSheet();
    } catch (e) { console.error(e); }
  };

  const removeBoat = async (boatId) => {
    if (!window.confirm("Bu tekneyi silmek istediginize emin misiniz? Rezervasyonlar 'Atanmamis' a tasinacak.")) return;
    try {
      await axios.delete(`${API}/api/boats/${boatId}`, { headers });
      fetchSheet();
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    axios.get(`${API}/api/respondio/templates`, { headers }).then(res => {
      setTemplates(res.data?.templates || []);
    }).catch(() => {});
  }, []);

  // Fetch phone message statuses - FAST: only get cached statuses, don't block on pending check
  const fetchPhoneStatuses = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/messages/phone-status?date=${dateStr}`, { headers });
      setPhoneStatuses(d.phone_statuses || {});
    } catch {}
  }, [dateStr]);

  // Background: check pending message statuses (non-blocking, runs separately)
  const checkPendingStatuses = useCallback(async () => {
    try {
      await axios.post(`${API}/api/messages/check-all-pending`, {}, { headers });
      // After check, refresh phone statuses
      const { data: d } = await axios.get(`${API}/api/messages/phone-status?date=${dateStr}`, { headers });
      setPhoneStatuses(d.phone_statuses || {});
    } catch {}
  }, [dateStr]);

  // Load phone statuses immediately (fast), then check pending in background
  useEffect(() => {
    fetchPhoneStatuses();
    // Delayed background check - don't block initial render
    const timer = setTimeout(() => checkPendingStatuses(), 3000);
    return () => clearTimeout(timer);
  }, [fetchPhoneStatuses, checkPendingStatuses]);

  // Batch validate all phone numbers
  const validateAllPhones = async () => {
    setValidating(true);
    try {
      const phones = data.rows
        .filter(r => r.customer_phone && r.customer_phone !== "--")
        .map(r => ({ phone: r.customer_phone, customer_name: r.customer_name, booking_id: r.booking_id }));

      if (phones.length === 0) { setValidating(false); return; }

      const { data: result } = await axios.post(`${API}/api/respondio/validate-phones`, { phones }, { headers });

      // Update phone statuses from results
      const newStatuses = { ...phoneStatuses };
      for (const r of result.results || []) {
        if (r.phone) {
          newStatuses[r.phone] = {
            status: r.status,
            has_whatsapp: r.has_whatsapp,
            last_message_at: new Date().toISOString(),
          };
        }
      }
      setPhoneStatuses(newStatuses);
      alert(`${result.whatsapp_valid || 0} numara WhatsApp'ta aktif, ${result.whatsapp_invalid || 0} numara bulunamadi`);
    } catch (err) {
      alert("Dogrulama hatasi: " + (err.response?.data?.detail || err.message));
    }
    setValidating(false);
  };

  const updateRow = async (rowId, updates) => {
    setSaving(true);
    // Optimistic update - update local state immediately
    setData(prev => {
      const newRows = prev.rows.map(r => {
        if (r.id !== rowId) return r;
        const updated = { ...r };
        if (updates.status !== undefined) updated.status = updates.status;
        if (updates.boat_id !== undefined) updated.boat_id = updates.boat_id;
        if (updates.locked !== undefined) updated.locked = updates.locked;
        if (updates.notes !== undefined) updated.op_notes = updates.notes;
        if (updates.manual_edits) {
          Object.entries(updates.manual_edits).forEach(([k, v]) => { updated[k] = v; });
        }
        return updated;
      });
      return { ...prev, rows: newRows };
    });
    try {
      await axios.put(`${API}/api/sheet/row/${rowId}`, updates, { headers });
    } catch (err) {
      console.error("Update error:", err);
      // Rollback on error - refetch
      fetchSheet();
    }
    setSaving(false);
  };

  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const rescheduleRow = (row) => {
    if (!row?.id) return;
    setRescheduleTarget(row);
  };
  const doReschedule = async (newDate) => {
    const row = rescheduleTarget;
    if (!row?.id || !newDate) return;
    try {
      const res = await axios.patch(`${API}/api/sheet/row/${row.id}/reschedule`, { new_date: newDate }, { headers, validateStatus: () => true });
      if (res.status === 200) {
        setRescheduleTarget(null);
        fetchSheet();
      } else alert(res.data?.detail || "Tarih değiştirilemedi");
    } catch (e) { alert("Ağ hatası — tarih değiştirilemedi"); }
  };

  const handleStatusChange = async (row, newStatus) => {
    if (!row?.id) return;
    if (newStatus === "cancelled") {
      const reason = window.prompt("İptal nedeni? (Müşteri talebi / No-show / Hata-Mükerrer / Diğer)\nBoş bırakabilirsiniz:", "Müşteri talebi") || "";
      try {
        await axios.patch(`${API}/api/sheet/row/${row.id}/cancel`, { cancelled: true, reason }, { headers, validateStatus: () => true });
        fetchSheet();
      } catch (e) { alert("İptal edilemedi"); }
    } else if (row.status === "cancelled") {
      try {
        await axios.patch(`${API}/api/sheet/row/${row.id}/cancel`, { cancelled: false }, { headers, validateStatus: () => true });
        if (newStatus !== "pending") updateRow(row.id, { status: newStatus }); else fetchSheet();
      } catch (e) { alert("Geri alınamadı"); }
    } else {
      updateRow(row.id, { status: newStatus });
    }
  };


  const handleDragStart = (e, row) => { setDragRow(row); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e, boatId) => { e.preventDefault(); setDragOverBoat(boatId); };
  const handleDrop = async (e, boatId) => {
    e.preventDefault();
    setDragOverBoat(null);
    if (dragRow && dragRow.boat_id !== boatId) await updateRow(dragRow.id, { boat_id: boatId });
    setDragRow(null);
  };

  // Group rows by boat
  const boatGroups = [];
  const boatMap = {};
  for (const boat of data.boats) {
    boatMap[boat.id] = { boat, rows: [] };
  }
  for (const row of data.rows) {
    if (activeCategory === "unmapped" && (row.category_id != null || !(row.product_name || "").trim())) continue;
    if (!rowMatchesTags(row)) continue;
    const bid = row.boat_id;
    if (boatMap[bid]) {
      boatMap[bid].rows.push(row);
    } else if (data.boats.length > 0) {
      boatMap[data.boats[0].id].rows.push(row);
    }
  }
  for (const boat of data.boats) {
    if (boatMap[boat.id]) {
      boatMap[boat.id].rows.sort((a, b) => {
        const aCancelled = a.status === "cancelled" ? 1 : 0;
        const bCancelled = b.status === "cancelled" ? 1 : 0;
        if (aCancelled !== bCancelled) return aCancelled - bCancelled;
        return (a.row_order || 0) - (b.row_order || 0);
      });
      boatGroups.push(boatMap[boat.id]);
    }
  }

  // Compute global row numbering
  let globalRow = 0;
  const allDisplayRows = applyStaffFilter(data.rows.filter(r => {
    if (activeCategory === "unmapped") return r.category_id == null && (r.product_name || "").trim();
    return true;
  }).filter(rowMatchesTags));
  const activeRows = allDisplayRows.filter(r => r.status !== "cancelled");
  const totalPax = activeRows.reduce((s, r) => s + (r.total_pax || 0), 0);
  const totalAdult = activeRows.reduce((s, r) => s + (r.adult_count || 0), 0);
  const totalChild = activeRows.reduce((s, r) => s + (r.child_count || 0), 0);
  const totalInfant = activeRows.reduce((s, r) => s + (r.infant_count || 0), 0);
  const checkedIn = allDisplayRows.filter((r) => r.status === "checked-in").length;
  const cancelled = allDisplayRows.filter((r) => r.status === "cancelled").length;
  const categories = data.categories || [];

  // Column definitions matching Google Sheets style
  const cols = [
    { key: "select", label: "", w: "w-7" },
    { key: "row_num", label: "", w: "w-8" },
    { key: "time", label: "Saat", w: "w-[50px]" },
    { key: "hotel", label: "Otel", w: "max-w-[100px]" },
    { key: "agency", label: "Acenta", w: "w-[50px]" },
    { key: "option", label: "Opsiyon", w: "max-w-[120px]" },
    { key: "pax", label: "Pax", w: "w-[70px]" },
    { key: "code", label: "KOD", w: "w-[100px]" },
    { key: "extras", label: "Ekstra", w: "max-w-[80px]" },
    { key: "customer", label: "Musteri", w: "min-w-[120px]" },
    { key: "phone", label: "Telefon", w: "min-w-[110px]" },
    { key: "notes", label: "Not", w: "min-w-[80px]" },
    { key: "status", label: "Durum", w: "w-[80px]" },
    { key: "boat", label: "Tekne", w: "w-[80px]" },
    { key: "msg_status", label: "Mesaj", w: "w-[70px]" },
    { key: "whatsapp", label: "WP", w: "w-[32px]" },
    { key: "lock", label: "", w: "w-[28px]" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-900" data-testid="operations-sheet" style={{ fontFamily: "'Arial', sans-serif", maxWidth: "100vw", overflowX: "hidden" }}>
      <RescheduleModal
        open={!!rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        onConfirm={doReschedule}
        currentDate={dateStr}
        title={rescheduleTarget ? `${rescheduleTarget.customer_name || rescheduleTarget.confirmation_code} · ${rescheduleTarget.confirmation_code}` : ""}
      />
      {/* Toolbar */}
      <div className="bg-[#F0F4F9] border-b border-gray-300 px-3 py-1.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Ship className="w-5 h-5 text-green-700 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-800 tracking-tight truncate">OPERASYON</div>
            <div className="text-[10px] text-gray-500 truncate hidden sm:block">{dateDisplay}</div>
          </div>
        </div>
        {/* Desktop toolbar buttons */}
        <div className="hidden md:flex items-center gap-2">
          {saving && <span className="text-[11px] text-blue-600 flex items-center gap-1"><Save className="w-3 h-3 animate-pulse" /> Kaydediliyor...</span>}
          <button
            onClick={() => { setBulkMode(!bulkMode); setSelectedRows(new Set()); }}
            className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium ${bulkMode ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'}`}
            data-testid="bulk-select-btn"
          >
            <CheckCircle className="w-3 h-3" /> {bulkMode ? `Secili: ${selectedRows.size}` : "Toplu Sec"}
          </button>
          {bulkMode && selectedRows.size > 0 && (
            <select
              onChange={(e) => { if (e.target.value) bulkMoveToBoat(parseInt(e.target.value)); }}
              className="text-[11px] border border-blue-400 rounded px-1.5 py-1 bg-blue-50"
              data-testid="bulk-move-select"
              defaultValue=""
            >
              <option value="" disabled>Tekneye tasi...</option>
              {data.boats.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button
            onClick={() => setShowBoatManager(!showBoatManager)}
            className="flex items-center gap-1 px-2.5 py-1 rounded bg-white border border-gray-300 hover:bg-gray-50 text-[11px] font-medium text-gray-700"
            data-testid="boat-manager-btn"
          >
            <Ship className="w-3 h-3" /> Tekne Yonetimi
          </button>
          <Link
            to="/category-settings"
            className="flex items-center gap-1 px-2.5 py-1 rounded bg-white border border-gray-300 hover:bg-gray-50 text-[11px] font-medium text-gray-700"
            data-testid="category-settings-link"
          >
            <Settings className="w-3 h-3" /> Urun Esleme
          </Link>
          <button
            onClick={() => {
              const link = document.createElement('a');
              link.href = `${API}/api/export/sheet/${dateStr}`;
              link.setAttribute('download', `operasyon_${dateStr}.xlsx`);
              // Add auth token as query param for download
              const t = localStorage.getItem('auth_token');
              if (t) link.href += `?token=${encodeURIComponent(t)}`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            className="flex items-center gap-1 px-2.5 py-1 rounded bg-white border border-gray-300 hover:bg-gray-50 text-[11px] font-medium text-gray-700"
            data-testid="excel-export-btn"
          >
            <Download className="w-3 h-3" /> Excel
          </button>
          <button
            onClick={validateAllPhones}
            disabled={validating}
            className="flex items-center gap-1 px-2.5 py-1 rounded bg-green-600 hover:bg-green-700 text-[11px] font-medium text-white disabled:opacity-50"
            title="Tum numaralari WhatsApp'ta kontrol et"
            data-testid="validate-phones-btn"
          >
            {validating ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageCircle className="w-3 h-3" />}
            {validating ? "Kontrol..." : "WhatsApp"}
          </button>
          <button onClick={fetchSheet} className="p-1.5 rounded bg-white border border-gray-300 hover:bg-gray-50 text-gray-600" title="Yenile" data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={runAutoAssign}
            disabled={autoAssigning}
            title="Atanmamış rezervasyonları kurallara göre doğru bölmeye otomatik dağıt"
            className="px-2 py-1.5 rounded bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[11px] font-medium disabled:opacity-50 flex items-center gap-1"
            data-testid="ops-auto-assign-btn"
          >
            <Wand2 className="w-3 h-3" /> {autoAssigning ? "Dağıtılıyor..." : "Otomatik Dağıt"}
          </button>
          <button
            onClick={runRedistribute}
            disabled={autoAssigning}
            title="Mevcut atamaları kurallara göre yeniden diz (kilitli/iptal/Rezerve hariç)"
            className="px-2 py-1.5 rounded bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-[11px] font-medium disabled:opacity-50 flex items-center gap-1"
            data-testid="ops-redistribute-btn"
          >
            <RefreshCw className="w-3 h-3" /> Yeniden Dağıt
          </button>
          <button
            onClick={runHardRedistribute}
            disabled={autoAssigning}
            title="Bugün ve tüm ileri tarihli rezervasyonları kurallara göre yeniden dağıt (kilitli/iptal/Rezerve hariç)"
            className="px-2 py-1.5 rounded bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 text-[11px] font-medium disabled:opacity-50 flex items-center gap-1"
            data-testid="ops-hard-redistribute-btn"
          >
            <RefreshCw className="w-3 h-3" /> Hard Yeniden Dağıt
          </button>
          <button
            onClick={quickSync}
            disabled={syncing}
            className="px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-medium disabled:opacity-50 flex items-center gap-1"
            title="Bu gun icin Bokun'dan tum booking'leri senkronize et"
            data-testid="quick-sync-btn"
          >
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {syncing ? "Sync..." : "Sync"}
          </button>
        </div>
        {/* Mobile toolbar buttons */}
        <div className="flex md:hidden items-center gap-1.5">
          <button
            onClick={quickSync}
            disabled={syncing}
            className="p-2 rounded bg-emerald-600 text-white disabled:opacity-50"
            data-testid="mobile-sync-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
          <button onClick={fetchSheet} className="p-2 rounded bg-white border border-gray-300 text-gray-600" data-testid="mobile-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setMobileActions(!mobileActions)}
            className="p-2 rounded bg-white border border-gray-300 text-gray-600"
            data-testid="mobile-more-btn"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile Actions Dropdown */}
      {mobileActions && isMobile && (
        <div className="bg-white border-b border-gray-200 px-3 py-2 flex flex-wrap gap-2 md:hidden" data-testid="mobile-actions-panel">
          <button
            onClick={() => { setBulkMode(!bulkMode); setSelectedRows(new Set()); setMobileActions(false); }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium ${bulkMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            data-testid="mobile-bulk-btn"
          >
            <CheckCircle className="w-3.5 h-3.5" /> {bulkMode ? `Secili: ${selectedRows.size}` : "Toplu Sec"}
          </button>
          <button
            onClick={() => { setShowBoatManager(!showBoatManager); setMobileActions(false); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium"
          >
            <Ship className="w-3.5 h-3.5" /> Tekneler
          </button>
          <button
            onClick={() => { validateAllPhones(); setMobileActions(false); }}
            disabled={validating}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-100 text-green-800 text-xs font-medium disabled:opacity-50"
          >
            <MessageCircle className="w-3.5 h-3.5" /> {validating ? "Kontrol..." : "WP Kontrol"}
          </button>
          <Link
            to="/category-settings"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium"
          >
            <Settings className="w-3.5 h-3.5" /> Urun Esleme
          </Link>
          <button
            onClick={() => {
              const link = document.createElement('a');
              link.href = `${API}/api/export/sheet/${dateStr}`;
              const t = localStorage.getItem('auth_token');
              if (t) link.href += `?token=${encodeURIComponent(t)}`;
              link.setAttribute('download', `operasyon_${dateStr}.xlsx`);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setMobileActions(false);
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium"
          >
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
          {bulkMode && selectedRows.size > 0 && (
            <select
              onChange={(e) => { if (e.target.value) bulkMoveToBoat(parseInt(e.target.value)); }}
              className="text-xs border border-blue-400 rounded-lg px-3 py-2 bg-blue-50"
              defaultValue=""
            >
              <option value="" disabled>Tekneye tasi...</option>
              {data.boats.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
        </div>
      )}

      {/* Boat Manager Panel */}
      {showBoatManager && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3" data-testid="boat-manager-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-800">Tekne Yonetimi</span>
            <button onClick={() => setShowBoatManager(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {data.boats.map(b => (
              <div key={b.id} className="flex items-center gap-1 bg-white border border-gray-300 rounded px-2 py-1">
                {editingBoat === b.id ? (
                  <input
                    autoFocus
                    defaultValue={b.name}
                    onBlur={(e) => renameBoat(b.id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") renameBoat(b.id, e.target.value); if (e.key === "Escape") setEditingBoat(null); }}
                    className="text-[12px] border-b border-blue-400 outline-none w-24 bg-transparent"
                    data-testid={`boat-rename-input-${b.id}`}
                  />
                ) : (
                  <span
                    className="text-[12px] font-medium cursor-pointer hover:text-blue-600"
                    onClick={() => setEditingBoat(b.id)}
                    title="Isim degistirmek icin tikla"
                  >{b.name}</span>
                )}
                {b.name !== "Atanmamis" && (
                  <span className="flex items-center gap-0.5 ml-1 pl-1 border-l border-gray-200" title="Tekne kapasitesi (kisi)">
                    <span className="text-[9px] text-gray-400 uppercase">Kap</span>
                    <input
                      type="number"
                      min="0"
                      defaultValue={b.capacity || ""}
                      placeholder="-"
                      onBlur={(e) => updateBoatCapacity(b.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className="w-11 text-[11px] text-gray-700 border border-gray-200 rounded px-1 py-0.5 outline-none focus:border-blue-400"
                      data-testid={`boat-capacity-input-${b.id}`}
                    />
                  </span>
                )}
                {b.name !== "Atanmamis" && (
                  <button
                    onClick={() => removeBoat(b.id)}
                    className="text-red-400 hover:text-red-600 ml-1"
                    title="Tekneyi sil"
                    data-testid={`boat-delete-${b.id}`}
                  ><X className="w-3 h-3" /></button>
                )}
              </div>
            ))}
            <div className="flex items-center gap-1">
              <input
                value={newBoatName}
                onChange={(e) => setNewBoatName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addBoat(); }}
                placeholder="Yeni tekne..."
                className="text-[12px] border border-gray-300 rounded px-2 py-1 w-28 outline-none focus:border-blue-400"
                data-testid="new-boat-input"
              />
              <button
                onClick={addBoat}
                className="px-2 py-1 rounded bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700"
                data-testid="add-boat-btn"
              >Ekle</button>
            </div>
          </div>
        </div>
      )}

      {/* Date Nav + Category Tabs (tek satır, ince) */}
      <div className="bg-[#F0F4F9] border-b border-gray-300 px-2 flex items-center gap-1 shrink-0">
        <div className="flex items-center gap-0.5 shrink-0">
          <button onClick={() => setDate(subDays(date, 1))} className="p-1 rounded hover:bg-gray-200 text-gray-600" data-testid="prev-day-btn">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-semibold text-gray-800 px-1 whitespace-nowrap">{dateDisplay}</span>
          <button onClick={() => setDate(addDays(date, 1))} className="p-1 rounded hover:bg-gray-200 text-gray-600" data-testid="next-day-btn">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="w-px h-5 bg-gray-300 mx-0.5 shrink-0" />
        <div className="flex items-center gap-0 overflow-x-auto flex-1 min-w-0" data-testid="category-tabs">
          <TabBtn active={activeCategory === null} onClick={() => setActiveCategory(null)} color="#4285F4" label={`Tumu (${data.total})`} testId="tab-all" />
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.icon] || Ship;
            const catCount = data.rows.filter(r => r.category_id === cat.id).length;
            return <TabBtn key={cat.id} active={activeCategory === cat.id} onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)} color={cat.color} label={`${cat.name} (${catCount})`} icon={Icon} testId={`tab-${cat.slug}`} />;
          })}
          {data.unmapped_count > 0 && (
            <TabBtn active={activeCategory === "unmapped"} onClick={() => setActiveCategory(activeCategory === "unmapped" ? null : "unmapped")} color="#D97706" label={`Eslenmemis (${data.unmapped_count})`} icon={AlertTriangle} testId="tab-unmapped" />
          )}
        </div>
        <div className="text-[11px] text-gray-500 hidden md:block shrink-0 pl-2 whitespace-nowrap">
          {allDisplayRows.length} Rez · <strong>{totalPax}</strong> Kişi
          <span className="text-gray-400"> ({totalAdult}Y{totalChild > 0 ? ` ${totalChild}C` : ""}{totalInfant > 0 ? ` ${totalInfant}B` : ""})</span>
          {" · "}{checkedIn} C/I · {cancelled} İptal
          {data.unmapped_count > 0 && <span className="text-amber-600"> · {data.unmapped_count} eşlenmemiş</span>}
        </div>
      </div>

      {/* Etiket / Addon Filtre Cubugu */}
      {availableTags.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-2 py-1 flex items-center gap-1.5 overflow-x-auto shrink-0" data-testid="tag-filter-bar">
          <Tag className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-1" />
          {availableTags.map(t => {
            const active = activeTagFilters.includes(t.label);
            return (
              <button
                key={t.label}
                onClick={() => setActiveTagFilters(p => active ? p.filter(x => x !== t.label) : [...p, t.label])}
                className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all border ${active ? "text-white border-transparent shadow-sm" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                style={active ? { backgroundColor: t.bg } : {}}
                data-testid={`tag-filter-${t.label}`}
              >
                {t.label} <span className={active ? "opacity-80" : "text-gray-400"}>({t.count})</span>
              </button>
            );
          })}
          {activeTagFilters.length > 0 && (
            <button
              onClick={() => setActiveTagFilters([])}
              className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-1 whitespace-nowrap"
              data-testid="tag-filter-clear"
            >
              <X className="w-3 h-3" /> Temizle
            </button>
          )}
        </div>
      )}

      {/* Staff Filter Bar */}
      {staffAssignments.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-2 py-1 flex items-center gap-1.5 overflow-x-auto shrink-0" data-testid="staff-filter-bar">
          <Users className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-1" />
          <button
            onClick={() => setActiveStaffFilter(null)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${!activeStaffFilter ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid="staff-filter-all"
          >
            Hepsini Goster
          </button>
          {staffAssignments.map(a => {
            const staffMember = staff.find(s => s.id === a.user_id);
            return (
              <button
                key={a.id}
                onClick={() => setActiveStaffFilter(activeStaffFilter === a.id ? null : a.id)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${activeStaffFilter === a.id ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid={`staff-filter-${a.id}`}
              >
                {a.assignment_name}
              </button>
            );
          })}
          {/* Message List Button - visible when filter active */}
          {activeStaffFilter && (
            <>
              <div className="w-px h-5 bg-gray-300 mx-1 shrink-0" />
              <button
                onClick={() => setShowMessageList(true)}
                className="px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1"
                data-testid="message-list-btn"
              >
                <MessageCircle className="w-3 h-3" /> Mesaj Listesi ({allDisplayRows.filter(r => r.customer_phone && r.customer_phone !== "--" && r.status !== "cancelled").length})
              </button>
            </>
          )}
        </div>
      )}

      {/* Spreadsheet Body */}
      <div className={`flex-1 overflow-auto pb-16 md:pb-0 ${isMobile ? 'overflow-x-hidden' : ''}`} style={{ fontSize: "12px" }}>
        {loading ? (
          <div className="p-12 text-center text-gray-400">Yukleniyor...</div>
        ) : allDisplayRows.length === 0 ? (
          <div className="p-12 text-center text-gray-400">{activeCategory ? "Bu kategoride rezervasyon yok" : "Bu tarihte rezervasyon yok"}</div>
        ) : isMobile ? (
          /* === MOBILE CARD VIEW === */
          <div className="px-2 py-2 space-y-3 overflow-x-hidden" data-testid="mobile-card-view">
            {boatGroups.map(({ boat, rows: boatRows }) => {
              if (boatRows.length === 0 && activeCategory) return null;
              const activeBoatRows = boatRows.filter(r => r.status !== "cancelled");
              const sectionPax = activeBoatRows.reduce((s, r) => s + (r.total_pax || 0), 0);
              const boatColor = boat.name === "Atanmamis" ? "#FEF3C7" : boat.sort_order === 1 ? "#DCFCE7" : boat.sort_order === 2 ? "#DBEAFE" : boat.sort_order === 3 ? "#FDE8E8" : "#F3E8FF";
              return (
                <div key={boat.id} data-testid={`mobile-boat-${boat.id}`}>
                  {/* Boat Header */}
                  <div className="flex items-center justify-between px-2 py-2 rounded-t-lg" style={{ backgroundColor: boatColor }}>
                    <div className="flex items-center gap-2">
                      <Ship className="w-4 h-4 text-gray-700" />
                      <span className="text-sm font-bold text-gray-800">{boat.name}</span>
                    </div>
                    <div className="text-xs text-gray-600 font-medium">{boatRows.length} rez | {sectionPax} kisi</div>
                  </div>
                  {/* Booking Cards */}
                  {boatRows.length === 0 ? (
                    <div className="text-center text-gray-400 text-xs py-4 bg-gray-50 rounded-b-lg border border-gray-100">Rezervasyon yok</div>
                  ) : (
                    <div className="space-y-1">
                      {boatRows.map((row) => {
                        const isLocked = row.locked;
                        const isCancelled = row.status === "cancelled";
                        const isExpanded = expandedCard === row.id;
                        const ps = phoneStatuses[row.customer_phone];
                        const wpStatus = ps?.status;
                        const badges = getAllBadges(row.option_name, row.product_name, customTags, row.extras);
                        const statusConfig = STATUS_OPTIONS.find(s => s.value === row.status) || STATUS_OPTIONS[0];
                        const isNew = row.created_at && (Date.now() - new Date(row.created_at).getTime()) < 30 * 60 * 1000;
                        const msgMarked = isMessageMarked(row.customer_phone, row.booking_id);
                        return (
                          <SwipeCard
                            key={row.id}
                            disabled={isCancelled || isLocked}
                            onSwipeRight={() => {
                              if (row.status !== "checked-in") updateRow(row.id, { status: "checked-in" });
                            }}
                            onSwipeLeft={() => {
                              if (row.customer_phone && row.customer_phone !== "--") window.location.href = `tel:${row.customer_phone}`;
                            }}
                            rightLabel="Check-in"
                            leftLabel="Ara"
                            rightColor="#DCFCE7"
                            leftColor="#DBEAFE"
                          >
                          <div
                            className={`border rounded-lg transition-all overflow-hidden ${isCancelled ? "opacity-50 bg-red-50/50 border-red-200" : row.status === "checked-in" ? "bg-green-50/50 border-green-200" : isNew ? "bg-amber-50/70 border-amber-200" : msgMarked ? "bg-green-50/30 border-green-100" : "bg-white border-gray-200"} ${isExpanded ? "shadow-md" : ""}`}
                            style={{ maxWidth: "100%" }}
                            data-testid={`mobile-card-${row.id}`}
                          >
                            {/* Card Main - Always visible */}
                            <div className="px-3 py-2.5" onClick={() => setExpandedCard(isExpanded ? null : row.id)}>
                              {/* Flex layout with explicit overflow */}
                              <div className="flex items-center gap-1.5" style={{ width: "100%", overflow: "hidden" }}>
                                {/* Time */}
                                <div className="text-xs font-bold text-gray-800 shrink-0" style={{ width: 38 }}>{row.booking_time || "--"}</div>
                                {/* Name */}
                                <div className="text-sm font-semibold text-gray-900 truncate" style={{ flex: "1 1 0%", minWidth: 0 }}>{row.customer_name || "Isim yok"}</div>
                                {/* PAX */}
                                <div className="text-sm font-bold text-blue-700 shrink-0">{row.total_pax || 0}</div>
                                {/* Status */}
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap shrink-0" style={{ background: statusConfig.bg, color: statusConfig.text }}>
                                  {statusConfig.label}
                                </span>
                                {/* WP + Message Status */}
                                {row.customer_phone && row.customer_phone !== "--" ? (
                                  <>
                                    <MessageStatusBtn
                                      phone={row.customer_phone}
                                      bookingId={row.booking_id}
                                      customerName={row.customer_name}
                                      staffNotes={staffNotes}
                                      onSelect={(type) => saveStaffNote(row.customer_phone, row.booking_id, type, row.customer_name || "")}
                                      onRemove={(noteId) => { axios.delete(`${API}/api/staff/notes/${noteId}`, { headers }).then(() => {
                                        axios.get(`${API}/api/staff/notes`, { headers }).then(res => {
                                          const nm = {}; (res.data?.notes || []).forEach(n => { const k = n.customer_phone || `bid_${n.booking_id}`; if (!nm[k]) nm[k] = []; nm[k].push(n); }); setStaffNotes(nm);
                                        });
                                      }); }}
                                    />
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setWhatsappModal(row); }}
                                      className={`p-1 rounded shrink-0 ${
                                        wpStatus === "delivered" || wpStatus === "read" ? "bg-green-100 text-green-700" :
                                        wpStatus === "failed" ? "bg-red-100 text-red-600" :
                                        wpStatus === "sent" ? "bg-blue-100 text-blue-600" :
                                        "text-green-600"
                                      }`}
                                      data-testid={`mobile-wp-btn-${row.id}`}
                                    >
                                      <MessageCircle className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : null}
                              </div>
                              {/* Second row: Agency + Badges + Staff */}
                              <div className="flex items-center gap-1 mt-0.5 ml-10" style={{ overflow: "hidden" }}>
                                <span className="text-[10px] font-semibold text-gray-400">{extractAgency(row.product_name)}</span>
                                {badges.slice(0, 3).map((b, i) => (
                                  <span key={i} className="text-[9px] font-bold px-1 rounded" style={{ background: b.bg, color: b.color }}>{b.label}</span>
                                ))}
                                {isLocked && <Lock className="w-3 h-3 text-amber-500" />}
                                {getAssignedStaff(row).map((s, i) => (
                                  <span key={i} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200" data-testid={`staff-tag-${row.id}`}>{s.name}</span>
                                ))}
                              </div>
                              {/* Third row: Option description */}
                              <div className="ml-10 mt-0.5" style={{ overflow: "hidden" }}>
                                <span className="text-[9px] italic text-gray-400 truncate block" data-testid={`mobile-option-${row.id}`}>{getOptionDesc(row.product_name)}</span>
                              </div>
                            </div>
                            {/* Expanded Details */}
                            {isExpanded && (
                              <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-2" data-testid={`mobile-card-details-${row.id}`}>
                                {/* Option */}
                                <div className="text-xs text-gray-700">
                                  <span className="font-semibold text-gray-500">Opsiyon: </span>
                                  {row.option_name || row.product_name || "-"}
                                </div>
                                {/* Phone */}
                                <div className="flex items-center justify-between">
                                  <div className="text-xs text-gray-600 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    <span className="font-mono">{row.customer_phone || "--"}</span>
                                  </div>
                                  {row.customer_phone && row.customer_phone !== "--" && (
                                    <a href={`tel:${row.customer_phone}`} className="text-xs text-blue-600 underline">Ara</a>
                                  )}
                                </div>
                                {/* Confirmation code */}
                                {row.confirmation_code && (
                                  <div className="text-xs"><span className="font-semibold text-gray-500">Kod: </span><span className="font-mono text-green-700">{row.confirmation_code}</span></div>
                                )}
                                {/* Hotel */}
                                {row.hotel_name && (
                                  <div className="text-xs"><span className="font-semibold text-gray-500">Otel: </span>{row.hotel_name}</div>
                                )}
                                {/* Notes */}
                                {row.notes && (
                                  <div className="text-xs"><span className="font-semibold text-gray-500">Not: </span>{row.notes}</div>
                                )}
                                {/* Staff Notes */}
                                {(() => {
                                  const noteKey = row.customer_phone || `bid_${row.booking_id}`;
                                  const notes = staffNotes[noteKey] || [];
                                  return (
                                    <div className="space-y-1">
                                      {notes.length > 0 && notes.slice(0, 3).map((n, i) => (
                                        <div key={n.id || i} className={`text-[11px] px-2 py-1 rounded ${n.note_type === "ulasti" ? "bg-green-50 text-green-700" : n.note_type === "ulasamadi" ? "bg-red-50 text-red-700" : "bg-gray-50 text-gray-600"}`}>
                                          {n.note_type === "ulasti" ? "Ulasti" : n.note_type === "ulasamadi" ? "Ulasamadi" : n.note_type === "mesaj_atti" ? "Mesaj atti" : n.note_type === "aradi" ? "Aradi" : "Not"}: {n.note_text || "-"}
                                        </div>
                                      ))}
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setNoteModal({ phone: row.customer_phone, booking_id: row.booking_id, name: row.customer_name }); }}
                                        className="text-[11px] text-blue-600 underline"
                                        data-testid={`add-note-${row.id}`}
                                      >
                                        + Not Ekle
                                      </button>
                                    </div>
                                  );
                                })()}
                                {/* Actions */}
                                <div className="flex items-center gap-2 pt-1">
                                  <select
                                    value={row.status || "pending"}
                                    onChange={(e) => handleStatusChange(row, e.target.value)}
                                    className="text-xs border rounded-lg px-2 py-1.5 flex-1"
                                    data-testid={`mobile-status-${row.id}`}
                                  >
                                    {STATUS_OPTIONS.map(so => <option key={so.value} value={so.value}>{so.label}</option>)}
                                  </select>
                                  <select
                                    value={row.boat_id || ""}
                                    onChange={(e) => updateRow(row.id, { boat_id: parseInt(e.target.value) || null })}
                                    className="text-xs border rounded-lg px-2 py-1.5 flex-1"
                                    data-testid={`mobile-boat-select-${row.id}`}
                                  >
                                    {data.boats.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                  </select>
                                  <button
                                    onClick={() => updateRow(row.id, { locked: !isLocked })}
                                    className={`p-1.5 rounded-lg border ${isLocked ? "bg-amber-50 border-amber-300 text-amber-600" : "border-gray-200 text-gray-400"}`}
                                    data-testid={`mobile-lock-${row.id}`}
                                  >
                                    {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          </SwipeCard>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* === DESKTOP TABLE VIEW === */
          <table className="w-full border-collapse" data-testid="sheet-table">
            {/* Column header */}
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#F0F4F9]">
                {cols.map((c) => (
                  <th key={c.key} className={`${c.w} px-1.5 py-1 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider border border-gray-300 bg-[#F0F4F9]`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {boatGroups.map(({ boat, rows: boatRows }) => {
                if (boatRows.length === 0 && activeCategory) return null;
                const activeBoatRows = boatRows.filter(r => r.status !== "cancelled");
                const sectionPax = activeBoatRows.reduce((s, r) => s + (r.total_pax || 0), 0);
                const sectionCheckedIn = boatRows.filter(r => r.status === "checked-in").length;

                return (
                  <BoatBlock
                    key={boat.id}
                    boat={boat}
                    rows={boatRows}
                    boats={data.boats}
                    cols={cols}
                    dateStr={dateStr}
                    sectionPax={sectionPax}
                    sectionCheckedIn={sectionCheckedIn}
                    globalRowStart={(() => { const s = globalRow; globalRow += boatRows.length; return s; })()}
                    dragRow={dragRow}
                    dragOverBoat={dragOverBoat}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragLeave={() => setDragOverBoat(null)}
                    onDrop={handleDrop}
                    updateRow={updateRow}
                    rescheduleRow={rescheduleRow}
                    handleStatusChange={handleStatusChange}
                    onWhatsApp={(row) => setWhatsappModal(row)}
                    phoneStatuses={phoneStatuses}
                    bulkMode={bulkMode}
                    selectedRows={selectedRows}
                    onToggleSelect={toggleRowSelect}
                    onSelectAll={() => selectAllInBoat(boat.id)}
                    getAssignedStaff={getAssignedStaff}
                    customTags={customTags}
                    toggleMessageSent={toggleMessageSent}
                    isMessageMarked={isMessageMarked}
                    staffNotes={staffNotes}
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* WhatsApp Message Modal */}
      {whatsappModal && (
        <WhatsAppModal
          row={whatsappModal}
          templates={templates}
          onClose={() => { setWhatsappModal(null); fetchPhoneStatuses(); }}
          headers={headers}
        />
      )}

      {/* Staff Note Modal */}
      {noteModal && (
        <NoteModal
          phone={noteModal.phone}
          bookingId={noteModal.booking_id}
          customerName={noteModal.name}
          onClose={() => setNoteModal(null)}
          onSave={saveStaffNote}
        />
      )}

      {/* Message List Panel */}
      {showMessageList && activeStaffFilter && (
        <MessageListPanel
          rows={allDisplayRows}
          phoneStatuses={phoneStatuses}
          staffNotes={staffNotes}
          onWhatsApp={(row) => { setShowMessageList(false); setWhatsappModal(row); }}
          onAddNote={(row) => { setNoteModal({ phone: row.customer_phone, booking_id: row.booking_id, name: row.customer_name }); }}
          onClose={() => setShowMessageList(false)}
          templates={templates}
          headers={headers}
          customTags={customTags}
          getAssignedStaff={getAssignedStaff}
        />
      )}
    </div>
  );
}

function MessageListPanel({ rows, phoneStatuses, staffNotes, onWhatsApp, onAddNote, onClose, templates, headers: authHeaders, customTags, getAssignedStaff }) {
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkStep, setBulkStep] = useState(null); // null | "template" | "confirm" | "sending" | "done"
  const [bulkTemplate, setBulkTemplate] = useState("custom");
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkProgress, setBulkProgress] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);

  const customers = rows
    .filter(r => r.customer_phone && r.customer_phone !== "--" && r.status !== "cancelled")
    .reduce((acc, r) => {
      if (!acc.find(x => x.customer_phone === r.customer_phone)) acc.push(r);
      return acc;
    }, []);

  const getSentStatus = (phone) => {
    const ps = phoneStatuses[phone];
    if (!ps) return "none";
    return ps.status || "none";
  };

  const getNoteLabel = (phone) => {
    const notes = staffNotes[phone];
    if (!notes || notes.length === 0) return null;
    const latest = notes[0];
    const labels = { ulasti: "Ulasti", ulasamadi: "Ulasamadi", mesaj_atti: "Mesaj Atti", aradi: "Aradi", diger: "Diger" };
    return { type: latest.note_type, label: labels[latest.note_type] || "Not", text: latest.note_text };
  };

  const sent = customers.filter(c => ["delivered", "read"].includes(getSentStatus(c.customer_phone)));
  const failed = customers.filter(c => ["failed"].includes(getSentStatus(c.customer_phone)));
  const pending = customers.filter(c => ["sent", "pending"].includes(getSentStatus(c.customer_phone)));
  const notSent = customers.filter(c => !["delivered", "read", "sent", "pending", "failed"].includes(getSentStatus(c.customer_phone)));

  const toggleSelect = (phone) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(phone) ? next.delete(phone) : next.add(phone);
      return next;
    });
  };
  const selectAllNotSent = () => setSelected(new Set(notSent.map(c => c.customer_phone)));
  const clearSelection = () => setSelected(new Set());

  const currentTemplate = (templates || []).find(t => (t.name || t.id) === bulkTemplate);
  const getTemplateBody = (tmpl) => {
    if (!tmpl) return "";
    if (tmpl.body) return tmpl.body;
    if (tmpl.components) {
      const parts = [];
      ["header", "body", "footer"].forEach(type => {
        const c = tmpl.components.find(x => x.type?.toLowerCase() === type);
        if (c?.text) parts.push(c.text);
      });
      if (parts.length) return parts.join("\n\n");
    }
    return tmpl.template || tmpl.text || "";
  };

  const startBulkSend = async () => {
    const selectedCustomers = customers.filter(c => selected.has(c.customer_phone));
    if (selectedCustomers.length === 0) return;
    setBulkSending(true);
    setBulkStep("sending");
    const progress = selectedCustomers.map(c => ({ phone: c.customer_phone, name: c.customer_name, booking_id: c.booking_id, option_name: c.option_name, product_name: c.product_name, status: "waiting" }));
    setBulkProgress([...progress]);

    for (let i = 0; i < progress.length; i++) {
      progress[i].status = "sending";
      setBulkProgress([...progress]);
      try {
        const payload = {
          booking_id: progress[i].booking_id || null,
          phone: progress[i].phone,
          customer_name: progress[i].name,
          template_id: bulkTemplate,
        };
        if (bulkTemplate === "custom") {
          payload.custom_message = bulkMessage;
        } else if (currentTemplate) {
          payload.template_name = currentTemplate.name;
          payload.template_namespace = currentTemplate.namespace || null;
        }
        const { data } = await axios.post(`${API}/api/messages/send-whatsapp`, payload, { headers: authHeaders });
        progress[i].status = "success";
        progress[i].detail = data.assigned_to ? `Gonderildi — ${data.assigned_to}'e atandi` : "Gonderildi";
      } catch (err) {
        progress[i].status = "failed";
        progress[i].detail = err.response?.data?.detail || "Gonderilemedi";
      }
      setBulkProgress([...progress]);
      // Rate limiting: 2 sec between messages
      if (i < progress.length - 1) await new Promise(r => setTimeout(r, 2000));
    }
    setBulkSending(false);
    setBulkStep("done");
    // Auto-check delivery status after 15 seconds
    setTimeout(async () => {
      try {
        await axios.post(`${API}/api/messages/check-all-pending`, {}, { headers: authHeaders });
      } catch {}
    }, 5000);
  };

  const successCount = bulkProgress.filter(p => p.status === "success").length;
  const failCount = bulkProgress.filter(p => p.status === "failed").length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center md:p-3" onClick={onClose}>
      <div className="bg-white rounded-t-xl md:rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] md:max-h-[85vh] flex flex-col overflow-hidden mb-16 md:mb-0" onClick={e => e.stopPropagation()} data-testid="message-list-panel">
        {/* Header */}
        <div className="px-4 py-3 bg-green-600 text-white flex items-center justify-between shrink-0">
          <div>
            <div className="font-semibold text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Mesaj Listesi</div>
            <div className="text-green-100 text-xs">{customers.length} musteri | {sent.length} iletildi | {failed.length > 0 ? `${failed.length} basarisiz | ` : ""}{pending.length > 0 ? `${pending.length} bekliyor | ` : ""}{notSent.length} gonderilmedi</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* Bulk Action Bar */}
        {bulkStep !== "sending" && bulkStep !== "done" && (
          <div className="px-4 py-2 bg-gray-50 border-b flex items-center gap-2 shrink-0 flex-wrap">
            {!bulkMode ? (
              <button onClick={() => { setBulkMode(true); setSelected(new Set(customers.map(c => c.customer_phone))); }} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg" data-testid="bulk-mode-btn">
                Toplu Gonder
              </button>
            ) : (
              <>
                <button onClick={() => setSelected(new Set(customers.map(c => c.customer_phone)))} className="px-2 py-1 bg-blue-100 text-blue-700 text-[11px] font-medium rounded">Tumunu Sec ({customers.length})</button>
                {notSent.length > 0 && <button onClick={selectAllNotSent} className="px-2 py-1 bg-red-100 text-red-700 text-[11px] font-medium rounded">Gonderilmemis ({notSent.length})</button>}
                <button onClick={clearSelection} className="px-2 py-1 bg-gray-100 text-gray-600 text-[11px] font-medium rounded">Temizle</button>
                <span className="text-[11px] text-gray-500">{selected.size} secili</span>
                <div className="flex-1" />
                {selected.size > 0 && !bulkStep && (
                  <button onClick={() => setBulkStep("template")} className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg" data-testid="bulk-next-btn">
                    Devam ({selected.size})
                  </button>
                )}
                <button onClick={() => { setBulkMode(false); clearSelection(); setBulkStep(null); }} className="px-2 py-1 text-gray-400 text-[11px] rounded hover:text-red-500">Iptal</button>
              </>
            )}
          </div>
        )}

        {/* Bulk Template Selection */}
        {bulkStep === "template" && (
          <div className="px-4 py-3 border-b bg-blue-50 space-y-3 shrink-0">
            <div className="text-sm font-bold text-gray-800">{selected.size} musteriye mesaj gonderilecek</div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Sablon Sec</label>
              <select value={bulkTemplate} onChange={e => setBulkTemplate(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="bulk-template-select">
                {(templates || []).map(t => {
                  const val = t.name || t.id;
                  return <option key={val} value={val}>{t.is_custom ? "Serbest Mesaj Yaz" : t.name}{t.status && t.status.toUpperCase() !== "APPROVED" ? ` (${t.status})` : ""}</option>;
                })}
              </select>
            </div>
            {bulkTemplate === "custom" ? (
              <div>
                <textarea value={bulkMessage} onChange={e => setBulkMessage(e.target.value)} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="Mesajinizi yazin..." data-testid="bulk-message-input" />
              </div>
            ) : currentTemplate && !currentTemplate.is_custom ? (
              <div className="bg-[#e5ddd5] rounded-lg p-3">
                <div className="bg-white rounded-lg p-3 max-w-[85%] ml-auto text-sm text-gray-800 whitespace-pre-line shadow-sm">{getTemplateBody(currentTemplate) || "Sablon icerigi"}</div>
              </div>
            ) : null}
            <div className="flex gap-2">
              <button onClick={() => setBulkStep(null)} className="px-3 py-2 text-sm border rounded-lg text-gray-600">Geri</button>
              <button
                onClick={() => setBulkStep("confirm")}
                disabled={bulkTemplate === "custom" && !bulkMessage.trim()}
                className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="bulk-confirm-btn"
              >
                Onizle ve Onayla ({selected.size})
              </button>
            </div>
          </div>
        )}

        {/* Bulk Confirmation Screen */}
        {bulkStep === "confirm" && (
          <div className="px-4 py-3 border-b bg-amber-50 space-y-3 shrink-0 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <div className="text-sm font-bold">Gonderim Onayi</div>
            </div>
            <div className="bg-white rounded-lg border border-amber-200 p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Gonderilecek Mesaj:</div>
              {bulkTemplate === "custom" ? (
                <div className="text-sm text-gray-800 whitespace-pre-line bg-gray-50 rounded p-2">{bulkMessage}</div>
              ) : (
                <div className="text-sm text-gray-800">Sablon: <span className="font-semibold">{currentTemplate?.name || bulkTemplate}</span></div>
              )}
            </div>
            <div className="bg-white rounded-lg border border-amber-200 p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Alicilar ({selected.size} kisi):</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {customers.filter(c => selected.has(c.customer_phone)).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-700 py-0.5">
                    <span className="font-medium w-5 text-gray-400">{i + 1}.</span>
                    <span className="font-semibold">{c.customer_name}</span>
                    <span className="text-gray-400 font-mono text-[10px]">{c.customer_phone}</span>
                    <div className="flex gap-0.5 ml-auto">
                      {getAllBadges(c.option_name, c.product_name, customTags).slice(0, 3).map((b, bi) => (
                        <span key={bi} className="text-[8px] font-bold px-1 rounded text-white" style={{ background: b.bg }}>{b.label}</span>
                      ))}
                    </div>
                    <button onClick={() => toggleSelect(c.customer_phone)} className="p-0.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Listeden cikar">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setBulkStep("template")} className="px-4 py-2.5 text-sm border rounded-lg text-gray-600 font-medium">Geri Don</button>
              <button
                onClick={startBulkSend}
                disabled={selected.size === 0}
                className="flex-1 px-4 py-2.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-send-confirm-btn"
              >
                <Send className="w-4 h-4" /> EVET, {selected.size} Kisi&apos;ye Gonder
              </button>
            </div>
          </div>
        )}

        {/* Bulk Progress */}
        {(bulkStep === "sending" || bulkStep === "done") && (
          <div className="px-4 py-3 border-b space-y-2 shrink-0">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-gray-800">
                {bulkStep === "sending" ? "Gonderiliyor..." : "Gonderim Tamamlandi"}
              </div>
              <div className="text-xs text-gray-500">
                <span className="text-green-600 font-bold">{successCount}</span> basarili
                {failCount > 0 && <span className="text-red-600 font-bold ml-2">{failCount} basarisiz</span>}
                {" / "}{bulkProgress.length}
              </div>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="h-2 rounded-full transition-all bg-green-500" style={{ width: `${((successCount + failCount) / Math.max(bulkProgress.length, 1)) * 100}%` }} />
            </div>
          </div>
        )}

        {/* Customer List */}
        <div className="flex-1 overflow-y-auto">
          {/* Bulk Progress Details */}
          {(bulkStep === "sending" || bulkStep === "done") ? (
            <div className="divide-y">
              {bulkProgress.map((p, i) => (
                <div key={i} className="px-4 py-2 flex items-start gap-3" data-testid={`bulk-progress-${i}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    p.status === "success" ? "bg-green-100 text-green-600" :
                    p.status === "failed" ? "bg-red-100 text-red-600" :
                    p.status === "sending" ? "bg-blue-100 text-blue-600" :
                    "bg-gray-100 text-gray-400"
                  }`}>
                    {p.status === "success" ? <CheckCircle className="w-4 h-4" /> :
                     p.status === "failed" ? <XCircle className="w-4 h-4" /> :
                     p.status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> :
                     <Clock className="w-3.5 h-3.5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{p.name}</div>
                    <div className="text-[10px] text-gray-400 font-mono">{p.phone}</div>
                    <div className="flex flex-wrap items-center gap-1 mt-0.5">
                      {getAllBadges(p.option_name, p.product_name, customTags).map((b, bi) => (
                        <span key={bi} className="text-[8px] font-bold px-1 py-0.5 rounded text-white" style={{ background: b.bg }}>{b.label}</span>
                      ))}
                    </div>
                    <div className="text-[9px] text-gray-400 mt-0.5 truncate">
                      {p.product_name || ""}
                      {p.option_name && !["standard rate", "standard", ""].includes((p.option_name || "").toLowerCase().trim()) ? ` — ${p.option_name}` : ""}
                    </div>
                  </div>
                  <div className={`text-[11px] font-medium shrink-0 max-w-[140px] text-right ${p.status === "success" ? "text-green-600" : p.status === "failed" ? "text-red-600" : "text-gray-400"}`}>
                    {p.status === "success" ? p.detail : p.status === "failed" ? p.detail : p.status === "sending" ? "Gonderiliyor..." : "Bekliyor"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Not Sent Section */}
              {notSent.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-red-50 text-red-700 text-xs font-bold uppercase tracking-wide sticky top-0 z-10">
                    Gonderilmemis ({notSent.length})
                  </div>
                  {notSent.map((row, i) => {
                    const note = getNoteLabel(row.customer_phone);
                    const isSelected = selected.has(row.customer_phone);
                    return (
                      <div key={row.id} className={`px-4 py-2.5 border-b border-gray-100 flex items-start gap-3 ${isSelected ? "bg-blue-50" : ""}`} data-testid={`msg-list-item-${i}`}>
                        {bulkMode && (
                          <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(row.customer_phone)} className="w-4 h-4 accent-blue-600 shrink-0 mt-1" />
                        )}
                        <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0 mt-0.5">
                          <XCircle className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{row.customer_name || "Isim yok"}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            <span className="font-mono">{row.customer_phone}</span>
                            <span className="text-gray-300 mx-1">|</span>
                            <span>{row.booking_time || "--"}</span>
                            <span className="text-gray-300 mx-1">|</span>
                            <span>{row.total_pax}K</span>
                            {getAssignedStaff && getAssignedStaff(row).length > 0 && (
                              <><span className="text-gray-300 mx-1">|</span><span className="text-purple-500 font-medium text-[10px]">{getAssignedStaff(row).map(s => s.name).join(", ")}</span></>
                            )}
                          </div>
                          {/* Full option info */}
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            <span className="text-[10px] font-semibold text-gray-400">{extractAgency(row.product_name)}</span>
                            {getAllBadges(row.option_name, row.product_name, customTags, row.extras).map((b, bi) => (
                              <span key={bi} className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white" style={{ background: b.bg }}>{b.label}</span>
                            ))}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5 leading-snug">
                            <span className="font-medium">{row.product_name || ""}</span>
                            {row.option_name && !["standard rate", "standard", ""].includes((row.option_name || "").toLowerCase().trim()) && (
                              <span className="text-gray-400"> — {row.option_name}</span>
                            )}
                          </div>
                          {note && (
                            <div className={`text-[10px] mt-0.5 font-medium ${note.type === "ulasamadi" ? "text-red-500" : note.type === "ulasti" ? "text-green-600" : "text-gray-500"}`}>
                              {note.label}{note.text ? `: ${note.text}` : ""}
                            </div>
                          )}
                        </div>
                        {!bulkMode && (
                          <>
                            <button onClick={() => onAddNote(row)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Not ekle">
                              <Star className="w-4 h-4" />
                            </button>
                            <button onClick={() => onWhatsApp(row)} className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors" data-testid={`msg-list-wp-${i}`}>
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Failed Section */}
              {failed.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-red-100 text-red-800 text-xs font-bold uppercase tracking-wide sticky top-0 z-10">
                    Basarisiz / WP Yok ({failed.length})
                  </div>
                  {failed.map((row, i) => {
                    const note = getNoteLabel(row.customer_phone);
                    const ps = phoneStatuses[row.customer_phone];
                    const isSelected = selected.has(row.customer_phone);
                    return (
                      <div key={row.id} className={`px-4 py-2.5 border-b border-gray-100 flex items-start gap-3 ${isSelected ? "bg-blue-50" : ""}`}>
                        {bulkMode && <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(row.customer_phone)} className="w-4 h-4 accent-blue-600 shrink-0 mt-1" />}
                        <div className="w-8 h-8 rounded-full bg-red-200 text-red-700 flex items-center justify-center shrink-0 mt-0.5">
                          <AlertTriangle className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{row.customer_name || "Isim yok"}</div>
                          <div className="text-xs text-red-600 font-medium">{ps?.error || "Mesaj iletilemedi"}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" /><span className="font-mono">{row.customer_phone}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            <span className="text-[10px] font-semibold text-gray-400">{extractAgency(row.product_name)}</span>
                            {getAllBadges(row.option_name, row.product_name, customTags, row.extras).map((b, bi) => (
                              <span key={bi} className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white" style={{ background: b.bg }}>{b.label}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pending Section */}
              {pending.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-wide sticky top-0 z-10">
                    Bekliyor ({pending.length})
                  </div>
                  {pending.map((row, i) => {
                    const isSelected = selected.has(row.customer_phone);
                    return (
                      <div key={row.id} className={`px-4 py-2.5 border-b border-gray-100 flex items-start gap-3 ${isSelected ? "bg-blue-50" : ""}`}>
                        {bulkMode && <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(row.customer_phone)} className="w-4 h-4 accent-blue-600 shrink-0 mt-1" />}
                        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0 mt-0.5">
                          <Clock className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{row.customer_name || "Isim yok"}</div>
                          <div className="text-xs text-amber-600">Respond.io'ya gonderildi, musteri iletimi bekleniyor...</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" /><span className="font-mono">{row.customer_phone}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Sent/Delivered Section */}
              {sent.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-green-50 text-green-700 text-xs font-bold uppercase tracking-wide sticky top-0 z-10">
                    Iletildi ({sent.length})
                  </div>
                  {sent.map((row, i) => {
                    const note = getNoteLabel(row.customer_phone);
                    const ps = phoneStatuses[row.customer_phone];
                    const isSelected = selected.has(row.customer_phone);
                    return (
                      <div key={row.id} className={`px-4 py-2.5 border-b border-gray-100 flex items-start gap-3 ${isSelected ? "bg-blue-50" : "opacity-70"}`} data-testid={`msg-list-sent-${i}`}>
                        {bulkMode && (
                          <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(row.customer_phone)} className="w-4 h-4 accent-blue-600 shrink-0 mt-1" />
                        )}
                        <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0 mt-0.5">
                          <CheckCircle className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-700 truncate">{row.customer_name || "Isim yok"}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            <span className="font-mono">{row.customer_phone}</span>
                            <span className="text-gray-300 mx-1">|</span>
                            <span>{ps?.status === "read" ? "Okundu" : ps?.status === "delivered" ? "Iletildi" : "Gonderildi"}</span>
                            {getAssignedStaff && getAssignedStaff(row).length > 0 && (
                              <><span className="text-gray-300 mx-1">|</span><span className="text-purple-500 font-semibold">{getAssignedStaff(row).map(s => s.name).join(", ")}'e atandi</span></>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            <span className="text-[10px] font-semibold text-gray-400">{extractAgency(row.product_name)}</span>
                            {getAllBadges(row.option_name, row.product_name, customTags, row.extras).map((b, bi) => (
                              <span key={bi} className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white" style={{ background: b.bg }}>{b.label}</span>
                            ))}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5 leading-snug">
                            <span className="font-medium">{row.product_name || ""}</span>
                            {row.option_name && !["standard rate", "standard", ""].includes((row.option_name || "").toLowerCase().trim()) && (
                              <span className="text-gray-400"> — {row.option_name}</span>
                            )}
                          </div>
                          {note && <div className="text-[10px] mt-0.5 text-green-600 font-medium">{note.label}</div>}
                        </div>
                        <button onClick={() => onAddNote(row)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Not ekle">
                          <Star className="w-4 h-4" />
                        </button>
                        <button onClick={() => onWhatsApp(row)} className="p-2 bg-gray-100 text-gray-400 rounded-lg hover:bg-green-100 hover:text-green-700 transition-colors">
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {customers.length === 0 && (
                <div className="p-8 text-center text-gray-400 text-sm">Bu filtrede mesaj atacak musteri yok</div>
              )}
            </>
          )}
        </div>

        {/* Footer for bulk done */}
        {bulkStep === "done" && (
          <div className="px-4 py-3 border-t bg-gray-50 shrink-0" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}>
            <button onClick={onClose} className="w-full px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg">Kapat</button>
          </div>
        )}
      </div>
    </div>
  );
}

function NoteModal({ phone, bookingId, customerName, onClose, onSave }) {
  const [noteType, setNoteType] = useState("ulasti");
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const types = [
    { value: "ulasti", label: "Ulasti", bg: "bg-green-100 text-green-800" },
    { value: "ulasamadi", label: "Ulasamadi", bg: "bg-red-100 text-red-800" },
    { value: "mesaj_atti", label: "Mesaj Atti", bg: "bg-blue-100 text-blue-800" },
    { value: "aradi", label: "Aradi", bg: "bg-amber-100 text-amber-800" },
    { value: "diger", label: "Diger", bg: "bg-gray-100 text-gray-800" },
  ];

  const handleSave = async () => {
    setSaving(true);
    await onSave(phone, bookingId, noteType, noteText);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center md:p-3" onClick={onClose}>
      <div className="bg-white rounded-t-xl md:rounded-xl shadow-2xl w-full max-w-sm overflow-hidden mb-16 md:mb-0" onClick={e => e.stopPropagation()} data-testid="note-modal">
        <div className="px-4 py-3 bg-gray-100 border-b">
          <div className="text-sm font-bold text-gray-800">Personel Notu</div>
          <div className="text-xs text-gray-500">{customerName || phone}</div>
        </div>
        <div className="p-4 space-y-3" style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom, 16px))" }}>
          <div className="flex flex-wrap gap-1.5">
            {types.map(t => (
              <button key={t.value} onClick={() => setNoteType(t.value)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${noteType === t.value ? `${t.bg} border-current` : "bg-white text-gray-400 border-gray-200"}`} data-testid={`note-type-${t.value}`}>
                {t.label}
              </button>
            ))}
          </div>
          <textarea
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={2}
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
            placeholder="Ek not (opsiyonel)..."
            data-testid="note-text-input"
          />
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 px-3 py-2 text-sm border rounded-lg text-gray-600">Iptal</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50" data-testid="save-note-btn">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, color, label, icon: Icon, testId }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium whitespace-nowrap border-b-2 transition-all ${
        active ? "border-current" : "border-transparent hover:bg-gray-200/50"
      }`}
      style={active ? { color, borderColor: color } : { color: "#5F6368" }}
      data-testid={testId}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}

function BoatBlock({ boat, rows, boats, cols, dateStr, sectionPax, sectionCheckedIn, globalRowStart, dragRow, dragOverBoat, onDragStart, onDragOver, onDragLeave, onDrop, updateRow, rescheduleRow, handleStatusChange, onWhatsApp, phoneStatuses, bulkMode, selectedRows, onToggleSelect, onSelectAll, getAssignedStaff, customTags, toggleMessageSent, isMessageMarked, staffNotes }) {
  const boatColor = boat.name === "Atanmamis" ? "#FEF3C7" : "#D1FAE5";
  const boatTextColor = boat.name === "Atanmamis" ? "#92400E" : "#065F46";
  const activeRows = rows.filter(r => r.status !== "cancelled");
  const sectionAdult = activeRows.reduce((s, r) => s + (r.adult_count || 0), 0);
  const sectionChild = activeRows.reduce((s, r) => s + (r.child_count || 0), 0);
  const sectionInfant = activeRows.reduce((s, r) => s + (r.infant_count || 0), 0);
  const cancelledCount = rows.length - activeRows.length;
  const allSelected = activeRows.length > 0 && activeRows.every(r => selectedRows.has(r.id));

  return (
    <>
      {/* Boat Section Header */}
      <tr
        className="transition-all"
        style={{ backgroundColor: dragOverBoat === boat.id ? "#DBEAFE" : boatColor }}
        onDragOver={(e) => onDragOver(e, boat.id)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, boat.id)}
        data-testid={`boat-group-${boat.id}`}
      >
        <td colSpan={cols.length} className="px-2 py-1.5 border border-gray-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {bulkMode && (
                <input type="checkbox" checked={allSelected} onChange={onSelectAll} className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" />
              )}
              <Ship className="w-4 h-4" style={{ color: boatTextColor }} />
              <span className="font-bold text-sm" style={{ color: boatTextColor }}>{boat.name}</span>
            </div>
            <span className="text-[11px] font-medium" style={{ color: boatTextColor }}>
              {activeRows.length} rez. | {sectionPax} kisi ({sectionAdult}Y{sectionChild > 0 ? ` ${sectionChild}C` : ""}{sectionInfant > 0 ? ` ${sectionInfant}B` : ""}) | {sectionCheckedIn} check-in{cancelledCount > 0 ? ` | ${cancelledCount} iptal` : ""}
            </span>
          </div>
        </td>
      </tr>

      {/* Data Rows */}
      {rows.length === 0 ? (
        <tr style={{ backgroundColor: dragOverBoat === boat.id ? "#EFF6FF" : "white" }}
            onDragOver={(e) => onDragOver(e, boat.id)} onDragLeave={onDragLeave} onDrop={(e) => onDrop(e, boat.id)}>
          <td colSpan={cols.length} className="px-2 py-3 text-center text-[11px] text-gray-400 border border-gray-200">
            {dragOverBoat === boat.id ? "Buraya birakin..." : "Rezervasyon yok"}
          </td>
        </tr>
      ) : (
        rows.map((row, idx) => {
          const rowNum = globalRowStart + idx + 1;
          const isCancelled = row.status === "cancelled";
          const isLocked = row.locked;
          const isCamKenari = getAllBadges(row.option_name, row.product_name, customTags, row.extras).some(b => b.label === "Cam Kenari");
          const statusOpt = STATUS_OPTIONS.find(s => s.value === row.status) || STATUS_OPTIONS[0];
          // Show separator before first cancelled row
          const prevRow = idx > 0 ? rows[idx - 1] : null;
          const showCancelSeparator = isCancelled && (!prevRow || prevRow.status !== "cancelled");

          return (
            <React.Fragment key={row.id}>
              {showCancelSeparator && (
                <tr>
                  <td colSpan={cols.length} className="px-2 py-1 text-center text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 tracking-wider">
                    IPTALLER ({cancelledCount})
                  </td>
                </tr>
              )}
            <tr
              draggable={!isLocked}
              onDragStart={(e) => {
                if (!_dragHandleActive) { e.preventDefault(); return; }
                _dragHandleActive = false;
                onDragStart(e, row);
              }}
              onDragEnd={() => { _dragHandleActive = false; }}
              className={`transition-colors ${isCancelled ? "opacity-40" : ""} ${dragRow?.id === row.id ? "opacity-20" : ""} ${isLocked ? "" : "hover:bg-blue-50/50"} ${selectedRows.has(row.id) ? "!bg-blue-100" : ""}`}
              style={{ backgroundColor: isCancelled ? "#FEF2F2" : isLocked ? "#FFFBEB" : selectedRows.has(row.id) ? "#DBEAFE" : isCamKenari ? "#FDF6E3" : "white", userSelect: "text" }}
              data-testid={`sheet-row-${row.id}`}
            >
              {/* Checkbox */}
              <td className="px-0.5 py-0.5 text-center border border-gray-200 bg-[#F8F9FA]">
                {bulkMode && !isCancelled && (
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.id)}
                    onChange={() => onToggleSelect(row.id)}
                    className="w-3 h-3 accent-blue-600 cursor-pointer"
                    data-testid={`row-checkbox-${row.id}`}
                  />
                )}
              </td>
              {/* Row Number + Drag Handle */}
              <td className="px-1 py-0.5 text-center text-[11px] text-gray-400 border border-gray-200 bg-[#F8F9FA]">
                {!isLocked && (
                  <GripVertical
                    className="w-3 h-3 text-gray-300 inline cursor-grab"
                    onMouseDown={() => { _dragHandleActive = true; }}
                  />
                )}
                <span className="ml-0.5">{rowNum}</span>
              </td>
              {/* Time */}
              <td className="px-1 py-0.5 text-center text-[11px] text-gray-600 border border-gray-200 font-mono" data-testid={`time-cell-${row.id}`}>
                {row.booking_time ? row.booking_time.substring(0, 5) : "-"}
              </td>
              {/* Hotel + Transfer Badge */}
              <td className={`px-1.5 py-0.5 border text-[12px] ${isTransferProduct(row.product_name) && !row.hotel_name ? "border-red-400 bg-red-50" : "border-gray-200"}`}>
                <div className="flex items-center gap-1">
                  {isTransferProduct(row.product_name) ? (
                    <span className="shrink-0 text-[9px] font-bold px-1 py-0 rounded bg-blue-600 text-white leading-tight" title="Hotel transferi dahil">TR</span>
                  ) : isKendiGelir(row.product_name) ? (
                    <span className="shrink-0 text-[9px] font-bold px-1 py-0 rounded bg-gray-300 text-gray-700 leading-tight" title="Kendi gelir">KG</span>
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <InlineEdit value={row.hotel_name} locked={isLocked} onSave={(v) => updateRow(row.id, { manual_edits: { hotel_name: v } })} />
                  </div>
                </div>
              </td>
              {/* Agency */}
              <td className="px-1.5 py-0.5 border border-gray-200 text-[11px] font-medium text-gray-600">
                {extractAgency(row.product_name)}
              </td>
              {/* Option with Badges */}
              <td className="px-1 py-0.5 border border-gray-200" data-testid={`option-cell-${row.id}`}>
                <div className="flex flex-wrap gap-0.5">
                  {row.cancel_reason && (
                    <span className="text-[8px] font-bold px-1 py-0 rounded leading-tight whitespace-nowrap" style={{ backgroundColor: "#dc2626", color: "#fff" }} title={`İptal nedeni: ${row.cancel_reason}`}>
                      İPTAL: {row.cancel_reason}
                    </span>
                  )}
                  {row.rescheduled_from && (
                    <span className="text-[8px] font-bold px-1 py-0 rounded leading-tight whitespace-nowrap" style={{ backgroundColor: "#7c3aed", color: "#fff" }} title={`Reschedule: ${row.rescheduled_from} → ${dateStr}`}>
                      ↻ {row.rescheduled_from}→{dateStr}
                    </span>
                  )}
                  {getAllBadges(row.option_name, row.product_name, customTags, row.extras).map((b, i) => (
                    <span key={i} className="text-[8px] font-bold px-1 py-0 rounded leading-tight whitespace-nowrap" style={{ backgroundColor: b.bg, color: b.color }} title={b.title}>
                      {b.label}
                    </span>
                  ))}
                </div>
                <div className="text-[11px] text-gray-700 leading-tight whitespace-normal break-words font-medium">
                  {row.option_name || shortProductLabel(row.product_name) || ""}
                </div>
                {row.product_name && (
                  <div className="text-[9px] text-gray-400 leading-tight whitespace-normal break-words" title="Bókun ürün başlığı">
                    {row.product_name}
                  </div>
                )}
              </td>
              {/* Pax Breakdown */}
              <td className="px-1 py-0.5 border border-gray-200 text-center" data-testid={`pax-cell-${row.id}`}>
                <div className="font-bold text-[13px]" style={{ color: "#1A73E8" }}>{row.total_pax}</div>
                <div className="text-[8px] leading-tight text-gray-500 flex justify-center gap-1">
                  <span title="Yetiskin">{row.adult_count || 0}Y</span>
                  <span title="Cocuk">{row.child_count || 0}C</span>
                  <span title="Bebek">{row.infant_count || 0}B</span>
                </div>
              </td>
              {/* Code */}
              <td className="px-1.5 py-0.5 border border-gray-200 font-mono text-[11px]" style={{ color: "#188038" }}>
                <div>{row.confirmation_code}</div>
                <button data-testid={`ops-reschedule-${row.id}`} onClick={(e) => { e.stopPropagation(); rescheduleRow(row); }}
                  title="Bu rezervasyonu başka bir güne taşı (reschedule)"
                  className="mt-0.5 text-[9px] font-semibold text-violet-600 hover:text-violet-800 hover:underline">
                  <i className="fa-solid fa-calendar-day mr-0.5" />Tarih değiştir
                </button>
              </td>
              {/* Extras */}
              <td className="px-1.5 py-0.5 border border-gray-200">
                {row.extras?.length > 0 ? (
                  <div className="flex flex-wrap gap-0.5">
                    {shortExtras(row.extras).map((ex, i) => (
                      <span key={i} className="text-[9px] font-semibold px-1 py-0 rounded text-white whitespace-nowrap" style={{ backgroundColor: extraColor(ex.label) }} title={row.extras.map(e => e.extra_name).join(", ")}>
                        {ex.label}{ex.qty > 1 ? ` ×${ex.qty}` : ""}
                      </span>
                    ))}
                  </div>
                ) : null}
              </td>
              {/* Customer */}
              <td className="px-1.5 py-0.5 border border-gray-200 text-[12px] font-medium">
                <InlineEdit value={row.customer_name} locked={isLocked} onSave={(v) => updateRow(row.id, { manual_edits: { customer_name: v } })} />
                {getAssignedStaff && getAssignedStaff(row).map((s, i) => (
                  <span key={i} className="text-[9px] font-bold px-1 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200 ml-0.5" data-testid={`desk-staff-tag-${row.id}`}>{s.name}</span>
                ))}
              </td>
              {/* Phone */}
              <td className="px-1.5 py-0.5 border border-gray-200 text-[11px] text-gray-600">
                <InlineEdit value={row.customer_phone} locked={isLocked} onSave={(v) => updateRow(row.id, { manual_edits: { customer_phone: v } })} />
              </td>
              {/* Notes */}
              <td className="px-1.5 py-0.5 border border-gray-200 text-[11px]">
                <InlineEdit value={row.op_notes} locked={isLocked} onSave={(v) => updateRow(row.id, { notes: v })} />
              </td>
              {/* Status */}
              <td className="px-1 py-0.5 border border-gray-200">
                <select
                  value={row.status}
                  onChange={(e) => handleStatusChange(row, e.target.value)}
                  className="text-[11px] font-medium px-1.5 py-0.5 rounded border-0 cursor-pointer w-full"
                  style={{ backgroundColor: statusOpt.bg, color: statusOpt.text }}
                  data-testid="status-select"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </td>
              {/* Boat */}
              <td className="px-1 py-0.5 border border-gray-200">
                <select
                  value={row.boat_id || ""}
                  onChange={(e) => updateRow(row.id, { boat_id: parseInt(e.target.value) })}
                  className="text-[11px] text-gray-600 bg-transparent border-0 cursor-pointer w-full"
                  data-testid={`boat-select-${row.id}`}
                >
                  {boats.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </td>
              {/* Mesaj Status */}
              <td className="px-0.5 py-0.5 border border-gray-200 text-center">
                {row.customer_phone && row.customer_phone !== "--" && toggleMessageSent ? (
                  <MessageStatusBtn
                    phone={row.customer_phone}
                    bookingId={row.booking_id}
                    customerName={row.customer_name}
                    staffNotes={staffNotes}
                    onSelect={(type) => toggleMessageSent(row.customer_phone, row.booking_id, row.customer_name, type)}
                    onRemove={(noteId) => toggleMessageSent(row.customer_phone, row.booking_id, null, null, noteId)}
                  />
                ) : null}
              </td>
              {/* WhatsApp */}
              <td className="px-0.5 py-0.5 border border-gray-200 text-center">
                {row.customer_phone ? (() => {
                  const ps = phoneStatuses[row.customer_phone];
                  const st = ps?.status;
                  let iconColor = "text-green-600 hover:text-green-800 hover:bg-green-50";
                  let title = "WhatsApp Mesaj Gonder";
                  let badge = null;
                  if (st === "delivered" || st === "read") {
                    iconColor = "text-green-600";
                    badge = <CheckCircle className="w-2 h-2 text-green-600 absolute -bottom-0.5 -right-0.5" />;
                    title = st === "read" ? "Okundu" : "Iletildi";
                  } else if (st === "sent") {
                    iconColor = "text-blue-500";
                    badge = <Clock className="w-2 h-2 text-blue-400 absolute -bottom-0.5 -right-0.5" />;
                    title = "Gonderildi (iletim bekleniyor)";
                  } else if (st === "failed") {
                    iconColor = "text-red-400";
                    badge = <XCircle className="w-2 h-2 text-red-500 absolute -bottom-0.5 -right-0.5" />;
                    title = "WhatsApp bulunamadi / Gonderilemedi";
                  }
                  return (
                    <button
                      onClick={() => onWhatsApp(row)}
                      className={`p-0.5 rounded relative ${iconColor}`}
                      title={title}
                      data-testid={`whatsapp-btn-${row.id}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      {badge}
                    </button>
                  );
                })() : (
                  <span className="text-gray-300" title="Telefon yok">
                    <MessageCircle className="w-3 h-3 inline" />
                  </span>
                )}
              </td>
              {/* Lock */}
              <td className="px-0.5 py-0.5 border border-gray-200 text-center">
                <button
                  onClick={() => updateRow(row.id, { locked: !isLocked })}
                  className={`p-0.5 rounded ${isLocked ? "text-amber-600" : "text-gray-300 hover:text-gray-500"}`}
                  title={isLocked ? "Kilidi Ac" : "Kilitle"}
                  data-testid={`lock-btn-${row.id}`}
                >
                  {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                </button>
              </td>
            </tr>
            </React.Fragment>
          );
        })
      )}

      {/* Summary Row */}
      {rows.length > 0 && (
        <tr style={{ backgroundColor: boatColor }}>
          <td className="border border-gray-300" />
          <td colSpan={3} className="px-1.5 py-1 border border-gray-300 text-[11px] font-bold text-right" style={{ color: boatTextColor }}>Toplam:</td>
          <td className="px-1.5 py-1 border border-gray-300 text-center font-bold text-[12px]" style={{ color: boatTextColor }}>
            {sectionPax}
            <span className="text-[9px] font-normal ml-1">({sectionAdult}Y {sectionChild}C {sectionInfant}B)</span>
          </td>
          <td colSpan={9} className="border border-gray-300" />
        </tr>
      )}

      {/* Spacer row between boats */}
      <tr><td colSpan={cols.length} className="h-1 bg-gray-100 border-x border-gray-200" /></tr>
    </>
  );
}


function DeliveryBadge({ status, deliveredAt, error }) {
  const configs = {
    delivered: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300", icon: CheckCircle, label: "Iletildi" },
    read: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300", icon: CheckCircle, label: "Okundu" },
    sent: { bg: "bg-sky-100", text: "text-sky-800", border: "border-sky-300", icon: Clock, label: "Gonderildi" },
    pending: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-300", icon: Clock, label: "Bekliyor" },
    failed: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300", icon: XCircle, label: "Basarisiz" },
    error: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300", icon: AlertTriangle, label: "Hata" },
  };
  const c = configs[status] || configs.pending;
  const Icon = c.icon;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.bg} ${c.text} ${c.border}`} data-testid="delivery-badge">
      <Icon className="w-3.5 h-3.5" />
      <span>{c.label}</span>
      {deliveredAt && <span className="font-normal opacity-70 ml-1">{new Date(deliveredAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>}
    </div>
  );
}

function PhoneValidator({ phone }) {
  if (!phone || phone === "--") return <span className="text-red-500 text-xs font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Numara bulunamadi</span>;
  const cleaned = phone.replace(/[^0-9+]/g, "");
  const hasCountryCode = cleaned.startsWith("+");
  const isLongEnough = cleaned.replace("+", "").length >= 10;
  if (!hasCountryCode) return <span className="text-amber-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Ulke kodu eksik olabilir</span>;
  if (!isLongEnough) return <span className="text-amber-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Numara cok kisa</span>;
  return <span className="text-emerald-600 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Gecerli numara</span>;
}

function WhatsAppModal({ row, templates, onClose, headers }) {
  const [selectedTemplate, setSelectedTemplate] = useState("custom");
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sentMessageId, setSentMessageId] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [activeTab, setActiveTab] = useState("send"); // send | history

  const isCustom = selectedTemplate === "custom";
  const currentTemplate = templates.find(t => (t.name || t.id) === selectedTemplate);

  // Fetch message history for this customer
  useEffect(() => {
    const fetchHistory = async () => {
      setLoadingHistory(true);
      try {
        const params = row.customer_phone ? `?phone=${encodeURIComponent(row.customer_phone)}&limit=10` : `?booking_id=${row.booking_id}&limit=10`;
        const { data } = await axios.get(`${API}/api/messages/history${params}`, { headers });
        setMessageHistory(data.messages || []);
      } catch { setMessageHistory([]); }
      setLoadingHistory(false);
    };
    fetchHistory();
  }, [row.customer_phone, row.booking_id]);

  const getTemplateBody = (tmpl) => {
    if (!tmpl) return "";
    if (tmpl.body) return tmpl.body;
    if (tmpl.components) {
      const parts = [];
      const header = tmpl.components.find(c => c.type?.toLowerCase() === "header");
      if (header?.text) parts.push(header.text);
      const body = tmpl.components.find(c => c.type?.toLowerCase() === "body");
      if (body?.text) parts.push(body.text);
      const footer = tmpl.components.find(c => c.type?.toLowerCase() === "footer");
      if (footer?.text) parts.push(footer.text);
      if (parts.length > 0) return parts.join("\n\n");
    }
    if (tmpl.template) return tmpl.template;
    return tmpl.text || "";
  };

  const previewText = isCustom ? customMessage : getTemplateBody(currentTemplate);

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    setDeliveryStatus(null);
    try {
      const payload = {
        booking_id: row.booking_id || null,
        phone: row.customer_phone,
        customer_name: row.customer_name,
        template_id: selectedTemplate,
      };
      if (isCustom) {
        payload.custom_message = customMessage;
      } else if (currentTemplate) {
        payload.template_name = currentTemplate.name;
        payload.template_namespace = currentTemplate.namespace || null;
      }
      const { data } = await axios.post(`${API}/api/messages/send-whatsapp`, payload, { headers });
      setResult({ success: true, message: data.message || "Mesaj basariyla gonderildi!", assignedTo: data.assigned_to });
      if (data.message_id) {
        setSentMessageId(data.message_id);
        // Auto-check delivery after 15 seconds
        const msgId = data.message_id;
        setTimeout(async () => {
          try {
            const { data: statusData } = await axios.post(`${API}/api/messages/${msgId}/check-status`, {}, { headers });
            setDeliveryStatus({ status: statusData.status, delivered_at: statusData.delivered_at, error: statusData.error_message });
          } catch {}
        }, 5000);
      }
      // Refresh history
      try {
        const params = row.customer_phone ? `?phone=${encodeURIComponent(row.customer_phone)}&limit=10` : `?booking_id=${row.booking_id}&limit=10`;
        const { data: hData } = await axios.get(`${API}/api/messages/history${params}`, { headers });
        setMessageHistory(hData.messages || []);
      } catch {}
    } catch (err) {
      const detail = err.response?.data?.detail || "Mesaj gonderilemedi";
      setResult({ success: false, message: detail });
    }
    setSending(false);
  };

  const checkDelivery = async () => {
    if (!sentMessageId) return;
    setCheckingStatus(true);
    try {
      const { data } = await axios.post(`${API}/api/messages/${sentMessageId}/check-status`, {}, { headers });
      setDeliveryStatus({ status: data.status, delivered_at: data.delivered_at, error: data.error_message });
    } catch {
      setDeliveryStatus({ status: "error" });
    }
    setCheckingStatus(false);
  };

  const approvedTemplates = templates.filter(t => t.is_custom || (t.status && t.status.toUpperCase() === "APPROVED"));
  const otherTemplates = templates.filter(t => !t.is_custom && (!t.status || t.status.toUpperCase() !== "APPROVED"));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center md:p-3" data-testid="whatsapp-modal" onClick={onClose}>
      <div className="bg-white rounded-t-xl md:rounded-xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden mb-16 md:mb-0" style={{ maxHeight: "calc(100dvh - 80px)" }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-green-600 to-green-700 rounded-t-xl shrink-0">
          <div className="flex items-center gap-3 text-white">
            <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base leading-tight">WhatsApp Mesaj Gonder</h3>
              <p className="text-green-100 text-xs">{row.customer_name || "Musteri"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors" data-testid="whatsapp-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Customer Info Bar */}
        <div className="px-5 py-3 bg-gray-50 border-b flex flex-wrap items-center gap-x-5 gap-y-2 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-sm">
              {(row.customer_name || "?")[0].toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-sm text-gray-900" data-testid="customer-name">{row.customer_name || "Isim yok"}</div>
              <div className="flex items-center gap-1.5 text-gray-600 text-xs">
                <Phone className="w-3 h-3" />
                <span className="font-mono" data-testid="customer-phone">{row.customer_phone || "--"}</span>
              </div>
            </div>
          </div>
          <PhoneValidator phone={row.customer_phone} />
          {row.confirmation_code && (
            <div className="text-xs font-mono text-green-800 bg-green-100 px-2.5 py-1 rounded-md border border-green-200" data-testid="confirmation-code">
              Kod: {row.confirmation_code}
            </div>
          )}
          {row.option_name && (
            <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
              {row.option_name}
            </div>
          )}
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b shrink-0">
          <button
            onClick={() => setActiveTab("send")}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${activeTab === "send" ? "text-green-700 border-green-600 bg-green-50/50" : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"}`}
            data-testid="tab-send"
          >
            <Send className="w-3.5 h-3.5 inline mr-1.5" />
            Mesaj Gonder
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 relative ${activeTab === "history" ? "text-green-700 border-green-600 bg-green-50/50" : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"}`}
            data-testid="tab-history"
          >
            <Clock className="w-3.5 h-3.5 inline mr-1.5" />
            Mesaj Gecmisi
            {messageHistory.length > 0 && (
              <span className="ml-1.5 text-[10px] bg-gray-200 text-gray-700 rounded-full px-1.5 py-0.5">{messageHistory.length}</span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === "send" ? (
            <div className="p-5 space-y-4">
              {/* Template Selector */}
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 block uppercase tracking-wide">Mesaj Sablonu</label>
                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                  {approvedTemplates.map(t => {
                    const val = t.name || t.id;
                    const isSelected = selectedTemplate === val;
                    return (
                      <label
                        key={val}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${isSelected ? "border-green-500 bg-green-50 ring-1 ring-green-200" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}
                        data-testid={`template-option-${val}`}
                      >
                        <input
                          type="radio"
                          name="wp-template"
                          value={val}
                          checked={isSelected}
                          onChange={(e) => setSelectedTemplate(e.target.value)}
                          className="accent-green-600 w-4 h-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800">{t.is_custom ? "Serbest Mesaj Yaz" : t.name}</div>
                          {!t.is_custom && (
                            <div className="text-xs text-gray-500 truncate mt-0.5">{getTemplateBody(t)?.substring(0, 80) || "Sablon icerigi"}{(getTemplateBody(t)?.length || 0) > 80 ? "..." : ""}</div>
                          )}
                        </div>
                        {t.is_custom ? (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Ozel</span>
                        ) : (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Onayli</span>
                        )}
                      </label>
                    );
                  })}
                  {otherTemplates.length > 0 && (
                    <div className="pt-2">
                      <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Onaylanmamis Sablonlar</div>
                      {otherTemplates.map(t => {
                        const val = t.name || t.id;
                        return (
                          <label key={val} className="flex items-center gap-3 p-2 rounded-lg border border-dashed border-gray-200 cursor-not-allowed opacity-50" data-testid={`template-option-${val}`}>
                            <input type="radio" disabled className="w-4 h-4" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-500">{t.name}</div>
                            </div>
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{t.status}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Message Input or Template Preview */}
              {isCustom ? (
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 block uppercase tracking-wide">Mesajiniz</label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm resize-none focus:ring-2 focus:ring-green-200 focus:border-green-400 transition-shadow"
                    placeholder="Musteriye gondermek istediginiz mesaji yazin..."
                    data-testid="custom-message-input"
                  />
                  <div className="text-[10px] text-gray-400 text-right mt-0.5">{customMessage.length} karakter</div>
                </div>
              ) : currentTemplate && !currentTemplate.is_custom ? (
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 block uppercase tracking-wide">Sablon Onizleme</label>
                  <div className="bg-[#e5ddd5] rounded-lg p-4" data-testid="message-preview">
                    <div className="bg-white rounded-lg shadow-sm p-3.5 max-w-[85%] ml-auto relative">
                      <div className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{previewText || "Sablon icerigi yukleniyor..."}</div>
                      <div className="flex items-center justify-end gap-1 mt-1.5">
                        <span className="text-[10px] text-gray-400">{new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>
                        <CheckCircle className="w-3 h-3 text-green-500" />
                      </div>
                    </div>
                  </div>
                  {currentTemplate.status && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs">
                      <span className="text-gray-500">Meta Durumu:</span>
                      <span className={`font-medium ${currentTemplate.status.toUpperCase() === "APPROVED" ? "text-emerald-600" : "text-amber-600"}`}>
                        {currentTemplate.status.toUpperCase() === "APPROVED" ? "Onayli" : currentTemplate.status}
                      </span>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Send Result */}
              {result && (
                <div className={`flex items-start gap-3 p-3.5 rounded-lg border ${result.success ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="send-result">
                  {result.success ? <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />}
                  <div>
                    <div className={`text-sm font-medium ${result.success ? "text-emerald-800" : "text-red-800"}`}>
                      {result.success ? "Mesaj Gonderildi" : "Gonderim Basarisiz"}
                    </div>
                    <div className={`text-xs mt-0.5 ${result.success ? "text-emerald-600" : "text-red-600"}`}>
                      {result.message}
                      {result.success && result.assignedTo && (
                        <span className="ml-1 font-semibold"> — Respond.io'da {result.assignedTo}'e atandi</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Delivery Status */}
              {deliveryStatus && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border" data-testid="delivery-status">
                  <span className="text-xs text-gray-600 font-medium">Iletim:</span>
                  <DeliveryBadge status={deliveryStatus.status} deliveredAt={deliveryStatus.delivered_at} error={deliveryStatus.error} />
                </div>
              )}
            </div>
          ) : (
            /* History Tab */
            <div className="p-5">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-10 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Gecmis yukleniyor...
                </div>
              ) : messageHistory.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm" data-testid="no-history">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Bu musteriye daha once mesaj gonderilmemis
                </div>
              ) : (
                <div className="space-y-2.5" data-testid="message-history-list">
                  {messageHistory.map((msg, i) => (
                    <div key={msg.id || i} className="flex items-start gap-3 p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors" data-testid={`history-item-${i}`}>
                      <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center ${
                        msg.status === "delivered" || msg.status === "read" ? "bg-emerald-100 text-emerald-700" :
                        msg.status === "failed" ? "bg-red-100 text-red-700" :
                        "bg-gray-100 text-gray-500"
                      }`}>
                        {msg.status === "delivered" || msg.status === "read" ? <CheckCircle className="w-4 h-4" /> :
                         msg.status === "failed" ? <XCircle className="w-4 h-4" /> :
                         <Send className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <DeliveryBadge status={msg.status || "pending"} />
                          <span className="text-[10px] text-gray-400 ml-auto shrink-0">
                            {msg.created_at ? new Date(msg.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "--"}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 truncate">{msg.message_text || msg.template_name || "Sablon mesaji"}</div>
                        {msg.error_message && <div className="text-[10px] text-red-500 mt-0.5">{msg.error_message}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-3.5 border-t bg-gray-50 flex items-center gap-2.5 shrink-0" style={{ paddingBottom: "max(14px, env(safe-area-inset-bottom, 14px))" }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            data-testid="whatsapp-cancel-btn"
          >
            Kapat
          </button>
          <div className="flex-1" />
          {sentMessageId && (
            <button
              onClick={checkDelivery}
              disabled={checkingStatus}
              className="px-3.5 py-2 text-sm border border-sky-300 rounded-lg text-sky-700 bg-sky-50 hover:bg-sky-100 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
              data-testid="check-delivery-btn"
            >
              {checkingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Durumu Kontrol Et
            </button>
          )}
          <button
            onClick={handleSend}
            disabled={sending || (isCustom && !customMessage.trim()) || activeTab === "history"}
            className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors shadow-sm"
            data-testid="whatsapp-send-btn"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Gonderiliyor...</>
            ) : (
              <><Send className="w-4 h-4" /> Mesaj Gonder</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
